import { createCompoundInterestTool, createPercentageTool } from "../factories";
import type { ToolConfig } from "../types";

export const retirementTools: ToolConfig[] = [
  // 1. Retirement Corpus Calculator
  {
    slug: "retirement-corpus-calculator",
    category: "retirement",
    title: "Retirement Corpus Calculator",
    shortTitle: "How Much You Need",
    description:
      "Determine your retirement corpus target accounting for inflation & lifestyle",
    keywords: [
      "retirement corpus",
      "retirement planning",
      "retirement savings needed",
      "retirement goal",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyExpense",
        label: "Current Monthly Expenses",
        prefix: "₹",
        min: 10000,
        max: 10000000,
        step: 5000,
        default: 100000,
      },
      {
        type: "number",
        key: "yearsToRetirement",
        label: "Years Until Retirement",
        suffix: "years",
        min: 1,
        max: 50,
        step: 1,
        default: 20,
      },
      {
        type: "number",
        key: "retirementYears",
        label: "Expected Retirement Duration",
        suffix: "years",
        min: 10,
        max: 60,
        step: 1,
        default: 30,
      },
      {
        type: "number",
        key: "inflationRate",
        label: "Expected Inflation Rate",
        suffix: "%",
        min: 1,
        max: 15,
        step: 0.5,
        default: 5,
      },
      {
        type: "number",
        key: "postRetirementReturn",
        label: "Expected Return in Retirement",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.5,
        default: 6,
      },
    ],
    calculate(v) {
      const monthlyExp = v.monthlyExpense as number;
      const yearsToRetire = v.yearsToRetirement as number;
      const retireYears = v.retirementYears as number;
      const inflation = (v.inflationRate as number) / 100;
      const returnRate = (v.postRetirementReturn as number) / 100;

      // Expenses at retirement (inflated)
      const expenseAtRetirement =
        monthlyExp * 12 * Math.pow(1 + inflation, yearsToRetire);

      // Corpus needed using annuity formula
      // PV = PMT × [1 - (1+r)^-n] / r
      const monthlyRate = returnRate / 12;
      const months = retireYears * 12;

      let corpus = 0;
      if (monthlyRate > 0) {
        corpus =
          expenseAtRetirement *
          (1 - Math.pow(1 + monthlyRate, -months)) /
          monthlyRate;
      } else {
        corpus = expenseAtRetirement * months;
      }

      return {
        currentMonthlyExp: Math.round(monthlyExp),
        expenseAtRetirement: Math.round(expenseAtRetirement),
        retirementDuration: Math.round(retireYears),
        corpusNeeded: Math.round(corpus),
        corporusForInflation: Math.round(corpus * 1.2),
      };
    },
    resultFields: [
      {
        key: "corpusNeeded",
        label: "Retirement Corpus Needed",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "expenseAtRetirement",
        label: "Annual Expense at Retirement",
        format: "currency",
      },
      {
        key: "retirementDuration",
        label: "Retirement Duration",
        format: "years",
      },
    ],
    content: {
      whatIs:
        "Your retirement corpus is the total amount you must accumulate by retirement age to sustain your desired lifestyle throughout your post-working years without financial stress. In India, where inflation averages 5-6% annually and life expectancy is rising, calculating an accurate corpus is critical for long-term security. This calculator accounts for three key factors: your current monthly expenses (indexed for inflation during your working years), the returns your corpus will generate post-retirement (typically 5-7% from a balanced debt-equity portfolio), and your expected retirement duration (commonly 30-35 years if retiring at 60). Most middle-class Indians need ₹1-2 crore for a comfortable retirement, depending on location, healthcare needs, and lifestyle choices. Without this planning, many retirees face the painful reality of depleting savings by age 80-85 or becoming dependent on family. The calculator helps you avoid this by showing the exact corpus needed and whether your current savings trajectory will meet it.",
      sections: [
        {
          title: "Who Should Use This Calculator?",
          body: "Salaried professionals, self-employed individuals, and business owners planning retirement. Anyone aged 25-55 who wants clarity on their retirement target. Essential for those transitioning from EPF/EPFO coverage to self-funded retirement. Critical for those over 50 who haven't started formal retirement planning. Parents planning to provide for retired parents while managing their own retirement needs.",
        },
        {
          title: "How Does It Work?",
          body: "The calculator inflates your current monthly expenses to retirement year using your assumed inflation rate (default 5%). It then calculates how much corpus is needed to withdraw this inflated amount monthly throughout retirement, accounting for investment returns during retirement. Formula: Corpus = Annual Expense at Retirement × [1 - (1+r)^-n] / r, where r is monthly return rate and n is months of retirement.",
        },
        {
          title: "Key Rules & Regulations in India",
          body: "EPFO members receive pension under EPS-95 (capped at ₹15,000/month) and PF lumpsum. VPF contributions increase corpus. Senior citizens (60+) have preferential tax treatment under Income Tax. PPF investments up to ₹1.5L per year offer tax-free corpus building. NPS provides defined returns under annuity post-60 years. Home mortgage must be cleared before retirement for housing expense clarity.",
        },
        {
          title: "Why Is Inflation Critical?",
          body: "If you need ₹50,000/month now at 5% inflation, you'll need ₹65,155/month 10 years later and ₹103,813/month 20 years later. Ignoring inflation is the #1 mistake Indians make in retirement planning. Your corpus must account for the falling purchasing power of the rupee over decades.",
        },
        {
          title: "Post-Retirement Returns Matter",
          body: "If corpus earns 0%, you deplete it faster. At 6% post-retirement returns, your corpus lasts 30-40% longer. A ₹1 crore corpus at 6% generates ₹6 lakh/year (₹50K/month) without touching principal. A balanced portfolio of PPF, FDs, bonds, and dividend stocks in retirement provides both safety and growth.",
        },
      ],
      faqs: [
        {
          question: "How do I calculate retirement corpus accurately?",
          answer:
            "Account for: current expenses, inflation (5-6%), investment returns in retirement (5-7%), life expectancy (85-90 years), healthcare costs. Use this calculator as a starting point, adjust for your risk tolerance.",
        },
        {
          question:
            "Is ₹1 crore enough to retire comfortably?",
          answer:
            "Depends on lifestyle. ₹1Cr at 6% return = ₹6L/year (₹50K/month). For basic lifestyle, yes. For comfort with travel/healthcare, may need ₹1.5-2Cr.",
        },
        {
          question:
            "Should I assume 0% return post-retirement?",
          answer:
            "No. Even after retirement, invest in debt/equity mix for 5-7% returns. This helps corpus last longer and beat inflation. Withdrawing without growth depletes quickly.",
        },
        {
          question:
            "What if I live longer than 30 years?",
          answer:
            "Plan for 35-40 years to be safe (assuming you retire at 60 and live to 95-100). Better to oversave than undersave. Extra corpus is bonus for heirs/legacy.",
        },
        {
          question:
            "Does my spouse's income/retirement affect my corpus?",
          answer:
            "Significantly. A dual-income couple can retire with less individual corpus (combined coverage). Plan jointly, assuming one income may stop earlier due to health/choice.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-planning",
        "retirement/retirement-income-calculator",
        "investment/sip-calculator",
      ],
    },
  },

  // 2. Retirement Planning
  {
    slug: "retirement-planning",
    category: "retirement",
    title: "Retirement Planning Calculator - Monthly Savings Needed",
    shortTitle: "Monthly Savings Needed",
    description:
      "Find your monthly SIP target to achieve your retirement corpus goal",
    keywords: [
      "retirement planning",
      "monthly savings",
      "retirement savings target",
      "sip for retirement",
    ],
    inputs: [
      {
        type: "number",
        key: "targetCorpus",
        label: "Target Retirement Corpus",
        prefix: "₹",
        min: 100000,
        max: 1000000000,
        step: 100000,
        default: 5000000,
      },
      {
        type: "number",
        key: "currentSavings",
        label: "Current Savings/Investments",
        prefix: "₹",
        min: 0,
        max: 1000000000,
        step: 100000,
        default: 1000000,
      },
      {
        type: "number",
        key: "yearsToRetirement",
        label: "Years Until Retirement",
        suffix: "years",
        min: 1,
        max: 50,
        step: 1,
        default: 20,
      },
      {
        type: "number",
        key: "expectedReturn",
        label: "Expected Annual Return",
        suffix: "% p.a.",
        min: 1,
        max: 30,
        step: 0.5,
        default: 12,
      },
    ],
    calculate(v) {
      const targetCorpus = v.targetCorpus as number;
      const currentSavings = v.currentSavings as number;
      const years = v.yearsToRetirement as number;
      const annual = v.expectedReturn as number;
      const monthlyRate = annual / 100 / 12;
      const months = years * 12;

      // Future value of current savings
      const fvCurrent =
        currentSavings * Math.pow(1 + monthlyRate, months);

      // Remaining corpus needed
      const remaining = Math.max(0, targetCorpus - fvCurrent);

      // Monthly SIP needed
      let monthlySIP = 0;
      if (monthlyRate > 0) {
        monthlySIP =
          remaining /
          ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate / (1 +
            monthlyRate));
      } else {
        monthlySIP = remaining / months;
      }

      return {
        targetCorpus: Math.round(targetCorpus),
        currentSavings: Math.round(currentSavings),
        fvCurrent: Math.round(fvCurrent),
        remaining: Math.round(remaining),
        monthlySIP: Math.round(monthlySIP),
        yearsToRetirement: years,
        totalToInvest: Math.round(monthlySIP * months),
      };
    },
    resultFields: [
      {
        key: "monthlySIP",
        label: "Monthly Savings Needed",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "remaining",
        label: "Gap After Current Savings",
        format: "currency",
      },
      {
        key: "targetCorpus",
        label: "Target Retirement Corpus",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "Once you know your retirement corpus target, the critical next step is determining how much you must save monthly via SIP (Systematic Investment Plan) to achieve it. This calculator reverse-engineers your monthly savings requirement by accounting for your current investments (which grow at assumed returns), your remaining gap, time horizon, and expected annual returns. For Indian investors, this is essential because it translates an abstract retirement goal into actionable monthly contributions. If the calculated monthly SIP seems unaffordable (more than 30% of net income), it signals you need to either extend retirement age, reduce your corpus target, or seek higher investment returns through calculated risk-taking. Many Indians adjust their SIP after salary increments, bonuses, and promotions. The calculator also shows total corpus growth from your current savings alone, helping you decide whether to prioritize immediate SIP contributions or first increase your current asset base.",
      sections: [
        {
          title: "Who Should Use This Calculator?",
          body: "Working professionals with 10-30 years to retirement who have a corpus target and want to know monthly savings needs. Self-employed individuals planning structured savings. Anyone who received a salary hike or bonus and wants to allocate a portion to retirement. Those 35-45 years old reassessing their retirement readiness. Anyone creating a detailed financial plan for banks, financial advisors, or loan applications.",
        },
        {
          title: "How Does It Work?",
          body: "Formula: FV of current savings = C × (1+r)^n; Remaining gap = Target - FV of current; Monthly SIP = Remaining / [(1+r)^n - 1] / r. The calculator compounds your existing investments forward, calculates the shortfall, then determines the monthly amount needed to bridge that gap using the future value of annuity formula. Higher expected returns reduce monthly SIP requirement.",
        },
        {
          title: "Key Rules & EPFO/NPS Considerations",
          body: "EPFO members automatically get EPF (basic) + EPS-95 (pension, capped at ₹15,000/month). VPF (Voluntary PF) up to 50% of salary can supplement. NPS contributions are tax-deductible (₹50K/year under 80CCD(1), ₹2L more under 80CCD(1B) for 50+). SIP investments in ELSS mutual funds get ₹1.5L tax deduction. Income from PPF, FDs in retirement is taxable; plan accordingly.",
        },
        {
          title: "Affordability & Flexibility",
          body: "If monthly SIP exceeds 30-40% of net income, it's unsustainable. Options: reduce corpus by ₹20-30L, extend retirement by 3-5 years (dramatically reduces monthly burden), or increase expected returns via tactical allocation (small-cap, mid-cap, or international diversification). Bonus and increment SIPs compound growth significantly over 20+ years.",
        },
        {
          title: "Return Rate Sensitivity",
          body: "Assuming 12% vs 10% returns can reduce monthly SIP by 15-20%. But overly aggressive assumptions (15%+ returns) can lead to retirement goal misses. Most financial advisors recommend conservative assumptions (8-10%) for safe planning. Adjust annually based on actual portfolio performance.",
        },
      ],
      faqs: [
        {
          question:
            "Is the monthly SIP amount realistic?",
          answer:
            "Depends on your income. If monthly SIP is >50% of your net income, target is too aggressive. Adjust by: (a) increasing years to retirement, (b) reducing target corpus, or (c) increasing expected returns (higher risk investments).",
        },
        {
          question:
            "What if I can't save the calculated monthly amount?",
          answer:
            "Save what you can. Start small (even ₹5K/month helps). Increase SIP after salary hikes using step-up feature. Or extend retirement by 2-3 years to reduce monthly burden.",
        },
        {
          question:
            "Should I adjust for tax?",
          answer:
            "The calculator shows pre-tax corpus. Account for: ELSS (tax-deductible), mutual fund taxes (LTCG, dividends), and EPF/NPS tax benefits. Your effective savings may be less after tax impact.",
        },
        {
          question:
            "What if stock markets crash after I retire?",
          answer:
            "Sequence-of-returns risk is real. Mitigate by: (a) keeping 2-3 years expenses in debt/FD, (b) using SWP (withdrawal plan) strategically, (c) planning for lower returns (assume 5-6%, not 8-12%).",
        },
        {
          question:
            "Should I invest in NPS, mutual funds, or both?",
          answer:
            "Use both: Maximize NPS (tax-deductible, lower fees, annuity benefit). Remaining corpus in diversified mutual funds (tax-efficient, flexible). Combination is optimal.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-corpus-calculator",
        "investment/sip-calculator",
        "investment/nps-calculator",
      ],
    },
  },

  // 3. Gratuity Calculator
  createPercentageTool({
    slug: "gratuity-calculator",
    category: "retirement",
    title: "Gratuity Calculator",
    shortTitle: "Gratuity Payout",
    description:
      "Calculate your gratuity payout using the Payment of Gratuity Act formula",
    keywords: [
      "gratuity",
      "retirement gratuity",
      "gratuity calculator",
      "employee gratuity",
      "severance pay",
    ],
    amountLabel: "Last Drawn Monthly Salary",
    rateLabel: "Years of Service",
    resultLabel: "Gratuity Amount",
    defaultAmount: 100000,
    maxAmount: 10000000,
    defaultRate: 30,
    maxRate: 50,
    whatIs:
      "Gratuity is a statutory benefit under India's Payment of Gratuity Act (1972), offering a lump-sum payment from your employer upon retirement or separation after completing 5 or more years of continuous service. The calculation follows a fixed formula: (15 × Last Monthly Salary × Years of Service) / 26 days, providing a farewell package that recognizes your years of contribution to the organization. Gratuity serves as an important component of retirement income for salaried employees, often constituting 20-30% of retirement corpus depending on your tenure and final salary. The benefit is capped at ₹20 lakh, and up to ₹10 lakh is entirely tax-free, making it a valuable financial cushion. Understanding your exact gratuity helps you include it in retirement planning, prevents disputes during exit, and allows you to invest the lump sum strategically—splitting between safe instruments (FDs, PPF) and growth assets (equity mutual funds, NPS). Many employees overlook this benefit or receive incorrect calculations; this tool ensures accuracy.",
    content: {
      sections: [
        {
          title: "Who Is Eligible for Gratuity?",
          body: "Employees who have completed 5+ years of continuous service under Payment of Gratuity Act. This applies to private sector employees, public sector employees (with variations), and contractual staff meeting tenure requirements. If you resign before 5 years, you forfeit gratuity unless the employer agrees otherwise. Involuntary separation (retrenchment, layoff) within 5 years still grants gratuity rights. Excluded: contractual workers without continuous service, apprentices, and workers in organizations with fewer than 10 employees (exemptions apply).",
        },
        {
          title: "How is Gratuity Calculated?",
          body: "Formula: (15 × Last Monthly Salary × Years of Service) / 26. Example: ₹1,00,000 monthly salary × 30 years = ₹17,30,769 gratuity. The 15/26 factor represents 15 days per month (26 working days). If you have partial years (10.5 years), round down to 10 years. Salary includes basic pay + dearness allowance (DA); doesn't include bonus, incentives, or perquisites in most cases.",
        },
        {
          title: "Tax Benefits Under Indian Law",
          body: "Up to ₹10 lakh is 100% tax-free. Amounts between ₹10L-20L are taxed as income in the year of receipt. Since most middle-class employee gratuities fall below ₹10L, they're fully exempt. Plan to receive this in the financial year of retirement to potentially avoid tax slab issues. High-earners with 30+ years service may exceed ₹20L cap (loss of excess benefit).",
        },
        {
          title: "Strategic Investment of Gratuity",
          body: "A one-time lump sum of ₹10-20L requires careful allocation. Recommended approach: 30% in fixed deposits/bonds (emergency buffer), 50% in diversified equity mutual funds (20-year horizon), 20% in NPS for tax-deductible annual contributions (₹2L limit, annuity at 60). Avoid lump-sum deployment into single high-risk instruments. Consider SIP allocation if you retire 5+ years before target investment horizon.",
        },
        {
          title: "Gratuity in Overall Retirement Planning",
          body: "Gratuity is supplementary income, not the primary retirement corpus. If your retirement need is ₹1 crore and gratuity is ₹20L, you must accumulate ₹80L separately through EPF, VPF, NPS, mutual funds, and personal savings. Track gratuity as a boost to your post-retirement liquid assets, not as a substitute for systematic retirement planning. Update your retirement corpus calculator to account for this benefit.",
        },
      ],
    },
    additionalResults(v) {
      const salary = v.amount as number;
      const years = v.rate as number;
      const gratuity = (15 * salary * years) / 26;
      const taxable = Math.max(0, gratuity - 1000000);
      const taxFreeBenefit = Math.min(gratuity, 1000000);
      return {
        gratuityAmount: Math.round(gratuity),
        taxFreeAmount: Math.round(taxFreeBenefit),
        taxableAmount: Math.round(taxable),
        capped: gratuity > 2000000,
      };
    },
    additionalResultFields: [
      {
        key: "gratuityAmount",
        label: "Total Gratuity (Before Cap)",
        format: "currency",
      },
      {
        key: "taxFreeAmount",
        label: "Tax-Free Portion",
        format: "currency",
      },
      {
        key: "taxableAmount",
        label: "Taxable Gratuity",
        format: "currency",
      },
    ],
    faqs: [
      {
        question: "When am I eligible for gratuity?",
        answer:
          "After 5+ years of continuous service. If separated involuntarily before 5 years (retrenchment), you get gratuity. If you resign before 5 years, no gratuity in most cases.",
      },
      {
        question:
          "Is gratuity tax-free?",
        answer:
          "Partially. Up to ₹10 lakh is tax-free. Excess is taxed as income in the year of receipt. For most employees (₹3-5L gratuity), fully tax-free.",
      },
      {
        question:
          "What if my gratuity exceeds ₹20 lakh?",
        answer:
          "Gratuity is capped at ₹20 lakh by law (as of 2024). Any excess is forfeited. Plan accordingly if you're high-salaried with 30+ years service.",
      },
      {
        question:
          "Can I invest my gratuity wisely?",
        answer:
          "Yes. Since it's a one-time lumpsum, consider: (a) 30% in FD/bonds (safe), (b) 50% in equity mutual funds (growth), (c) 20% in NPS for additional tax benefit + pension.",
      },
      {
        question:
          "Does gratuity count toward retirement savings?",
        answer:
          "Partially. Gratuity alone is insufficient. E.g., ₹20L gratuity + ₹50L retirement corpus = ₹70L total. If corpus needed is ₹1Cr, you still need ₹30L more. Plan accordingly.",
      },
    ],
    relatedSlugs: [
      "retirement/retirement-corpus-calculator",
      "retirement/epf-calculator",
      "retirement/retirement-planning",
    ],
  }),

  // 4. EPF Calculator
  {
    slug: "epf-calculator",
    category: "retirement",
    title: "EPF Calculator - Retirement Corpus at 8.25%",
    shortTitle: "EPF Maturity",
    description:
      "Project your EPF balance at retirement with guaranteed 8.25% returns",
    keywords: [
      "epf",
      "employee provident fund",
      "pf maturity",
      "retirement savings",
      "pension fund",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlySalary",
        label: "Monthly Basic Salary",
        prefix: "₹",
        min: 10000,
        max: 5000000,
        step: 5000,
        default: 100000,
      },
      {
        type: "number",
        key: "yearsOfService",
        label: "Years of Service",
        suffix: "years",
        min: 0,
        max: 50,
        step: 1,
        default: 30,
      },
      {
        type: "number",
        key: "annualIncrement",
        label: "Annual Salary Increment",
        suffix: "%",
        min: 0,
        max: 15,
        step: 0.5,
        default: 5,
      },
    ],
    calculate(v) {
      const monthly = v.monthlySalary as number;
      const years = v.yearsOfService as number;
      const increment = (v.annualIncrement as number) / 100;
      const monthlyRate = 0.0825 / 12; // 8.25% per annum
      const months = years * 12;

      let epfBalance = 0;
      let totalEmployeeContribution = 0;
      let totalEmployerContribution = 0;

      for (let m = 0; m < months; m++) {
        const year = Math.floor(m / 12);
        const currentSalary = monthly * Math.pow(1 + increment, year);

        // Employee contribution: 12% of salary
        // Employer contribution: 12% (8.33% to EPF, 3.67% to EPS-95)
        // Combined to EPF account: 12% + 12% = 24%
        const employeeContr = currentSalary * 0.12;
        const employerContr = currentSalary * 0.12;

        epfBalance = epfBalance * (1 + monthlyRate) + employeeContr + employerContr;
        totalEmployeeContribution += employeeContr;
        totalEmployerContribution += employerContr;
      }

      return {
        epfBalance: Math.round(epfBalance),
        yearsOfService: years,
        totalEmployeeContribution: Math.round(totalEmployeeContribution),
        totalEmployerContribution: Math.round(totalEmployerContribution),
        totalContribution: Math.round(
          totalEmployeeContribution + totalEmployerContribution
        ),
        interest: Math.round(
          epfBalance -
            totalEmployeeContribution -
            totalEmployerContribution
        ),
      };
    },
    resultFields: [
      {
        key: "epfBalance",
        label: "EPF Maturity Amount",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalContribution",
        label: "Total Contributions",
        format: "currency",
      },
      {
        key: "interest",
        label: "Interest Earned",
        format: "currency",
      },
      {
        key: "totalEmployerContribution",
        label: "Employer Contribution",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "The Employee Provident Fund (EPF) under EPFO is India's largest government-backed retirement savings scheme for private sector employees. Both you and your employer contribute 12% of your basic salary each month (24% total), creating a guaranteed, inflation-protected retirement fund. EPF currently earns 8.25% annual interest, set annually by the government and backed by sovereign guarantee. Unlike market-dependent investments, EPF is risk-free and entirely tax-exempt on withdrawal at retirement. For a ₹100,000 monthly salary employee, EPF accumulation over 30 years yields approximately ₹45-50 lakh. However, most financial advisors note that EPF alone (even ₹50 lakh) covers only 40-50% of a typical ₹1 crore retirement corpus need. This calculator projects your exact EPF maturity by factoring in salary increments, ensuring you know precisely how much EPF will contribute to your retirement. It also highlights the gap, motivating supplementary retirement savings through VPF, NPS, and mutual funds. Understanding EPF's role prevents over-reliance and ensures balanced retirement planning.",
      sections: [
        {
          title: "How EPF Membership Works",
          body: "EPF is compulsory for employees earning up to ₹15,000 monthly salary (as per EPFO rules). Above ₹15,000, it's optional but highly recommended. Your account is registered with EPFO portal (epfoservices.gov.in). Both employee and employer contributions are credited monthly. You get a universal account number (UAN) that moves with you across jobs. No action needed to join; employer registers you on EPF portal automatically.",
        },
        {
          title: "EPF Contribution Breakdown",
          body: "Monthly EPF = 12% of Basic Salary (employee) + 12% of Basic Salary (employer). From your 12%, ₹1.25% goes to administration and insurance. Employer's 12% is split: 8.33% to your EPF account and 3.67% to Employees' Pension Scheme (EPS-95). So your EPF account receives employee 12% + employer 8.33% = 20.33% of basic. At retirement, you also get EPS-95 pension (capped at ₹15,000/month).",
        },
        {
          title: "The 8.25% Interest Rate",
          body: "EPF interest is declared annually (usually in March) and is applied quarterly. Currently 8.25% (as of 2020, with variations 8-9% in recent years). Government guarantees this rate regardless of market conditions. Interest is compounded quarterly and is fully tax-free. This is lower than equity mutual funds (12% avg) but infinitely safer than any market-linked investment. Conservative but reliable for risk-averse retirees.",
        },
        {
          title: "Partial Withdrawals During Service",
          body: "You can withdraw 50% of EPF for medical emergencies (after 5 years of service), education, or home purchase (under specific conditions). Full withdrawal only at age 58-60 (varies by joining date) or on retirement. Partial withdrawals reduce final corpus but provide emergency liquidity. Most financial planners recommend avoiding partial withdrawals for non-essential expenses to preserve retirement corpus.",
        },
        {
          title: "EPF in Your Overall Retirement Plan",
          body: "EPF is a foundation but insufficient alone. With ₹1 lakh/month salary and 30 years service, EPF yields ~₹50L. If retirement corpus needed is ₹1 crore, EPF covers 50%. Use supplementary VPF (additional voluntary contributions to EPF), NPS (₹50K/year tax-deductible), and personal mutual fund SIPs to bridge the gap. Diversification reduces risk.",
        },
      ],
      faqs: [
        {
          question:
            "How much of EPF is in my account vs company?",
          answer:
            "Employee contribution (12%) is fully yours. Employer contribution (12%) is also credited to your EPF account; you own it all. At retirement, full balance is yours (tax-free).",
        },
        {
          question:
            "Can I withdraw EPF before retirement?",
          answer:
            "Limited withdrawals allowed: medical emergency (up to 50% after 5 years), higher education (50%, anytime), home purchase (can withdraw). Full withdrawal only at age 58-60 (depending on joining date).",
        },
        {
          question:
            "Is EPF interest rate fixed?",
          answer:
            "Declared annually by the government. Currently 8.25% (since 2020). Rate varies: 8-9% in recent years. Conservative but stable.",
        },
        {
          question:
            "What happens to EPF if I switch jobs?",
          answer:
            "Your EPF account continues. You can transfer EPF to new employer's account within 30 days of joining. Or keep it with previous employer (non-earning), then withdraw at retirement.",
        },
        {
          question:
            "Is EPF enough for retirement?",
          answer:
            "Typically not alone. Average EPF maturity: ₹20-50L depending on salary/years. If retirement corpus needed is ₹1Cr, EPF covers 20-50%. Supplement with NPS, mutual funds, real estate.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-corpus-calculator",
        "retirement/vpf-calculator",
        "retirement/eps-pension-calculator",
      ],
    },
  },

  // 5. VPF Calculator
  {
    slug: "vpf-calculator",
    category: "retirement",
    title: "VPF Calculator - Voluntary PF Extra Contribution",
    shortTitle: "VPF Planning",
    description:
      "Project additional retirement savings through tax-deductible VPF contributions",
    keywords: [
      "vpf",
      "voluntary provident fund",
      "extra pf contribution",
      "retirement savings",
      "tax savings",
    ],
    inputs: [
      {
        type: "number",
        key: "mandatoryPFMonthly",
        label: "Mandatory PF (12% of salary)",
        prefix: "₹",
        min: 1000,
        max: 500000,
        step: 1000,
        default: 12000,
      },
      {
        type: "number",
        key: "vpfMonthly",
        label: "Additional VPF Monthly Contribution",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 1000,
        default: 5000,
      },
      {
        type: "number",
        key: "yearsToRetirement",
        label: "Years Until Retirement",
        suffix: "years",
        min: 1,
        max: 50,
        step: 1,
        default: 20,
      },
    ],
    calculate(v) {
      const mandatoryPF = v.mandatoryPFMonthly as number;
      const vpf = v.vpfMonthly as number;
      const years = v.yearsToRetirement as number;
      const monthlyRate = 0.0825 / 12;
      const months = years * 12;

      const totalMonthly = mandatoryPF + vpf;

      // Future value of annuity
      const fv =
        totalMonthly *
        ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
        (1 + monthlyRate);

      const mandatoryFV =
        mandatoryPF *
        ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
        (1 + monthlyRate);

      const vpfFV = fv - mandatoryFV;

      const totalContribution = totalMonthly * months;
      const interest = fv - totalContribution;

      return {
        totalPF: Math.round(fv),
        mandatoryPF: Math.round(mandatoryFV),
        vpfAmount: Math.round(vpfFV),
        totalContribution: Math.round(totalContribution),
        interestEarned: Math.round(interest),
        vpfBenefit: Math.round(vpfFV - vpf * months),
      };
    },
    resultFields: [
      {
        key: "totalPF",
        label: "Total PF Maturity",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "vpfAmount",
        label: "VPF Balance",
        format: "currency",
      },
      {
        key: "interestEarned",
        label: "Total Interest Earned",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "VPF (Voluntary Provident Fund) is a powerful tax-deductible savings tool that allows EPFO members to contribute additional amounts beyond the mandatory 12% employee EPF contribution. VPF contributions earn the same government-guaranteed 8.25% interest but offer flexibility in contribution amounts and timing. Unlike NPS (which has specific contribution limits), VPF allows up to ₹7.5 lakh annually combined with mandatory EPF, fully deductible under Section 80C of the Income Tax Act. For a ₹100,000 salary employee already saving ₹12,000/month in EPF, adding ₹5,000/month VPF yields approximately ₹12-15 lakh extra at retirement (after interest compounding). VPF is ideal for high-income earners who want tax-deductible retirement savings beyond NPS caps, providing both forced savings discipline and substantial tax benefits. This calculator shows the power of supplementary VPF contributions, demonstrating how even modest additions compound significantly over 20-30 years.",
      sections: [
        {
          title: "How VPF Works Differently from EPF",
          body: "Mandatory EPF (12% from salary) goes to regular EPF account. VPF is voluntary, discretionary, and credited separately within the same EPF account (or as a distinct investment). Both earn 8.25%. VPF contributions are not deducted by employer automatically; you initiate via EPFO portal or HR. VPF is ideal for employees earning above ₹15,000/month who want maximum tax-deductible savings without relying on NPS alone.",
        },
        {
          title: "Tax Benefits & Section 80C Limits",
          body: "VPF contributions are 100% deductible under Section 80C (₹1.5L annual limit for all 80C investments combined). If you've already maxed NPS (₹2.5L under special 80CCD(1B) for 50+), VPF still gives you additional deductions. Combined EPF (mandatory + VPF) limit: ₹7.5L annually. For ₹5L salary, you can contribute ~₹40K/month to EPF + VPF combined.",
        },
        {
          title: "VPF Withdrawal Rules & Lock-in",
          body: "VPF has stricter withdrawal rules than mandatory EPF. You cannot withdraw VPF before 5 years of continuous contribution (except for retirement, death, or medical emergency with restrictions). This enforced discipline prevents emotional withdrawals and locks in compounding. At retirement, full VPF balance is accessible and tax-free. This contrasts with mutual fund withdrawals (taxed on gains).",
        },
        {
          title: "VPF vs NPS vs Mutual Funds",
          body: "VPF: Safe, 8.25% guaranteed, strictly tax-deductible, no market risk, limited withdrawal. NPS: Tax-deductible (₹50K + ₹2L for 50+), potential 10% returns, partial flexibility. MFs: Higher growth (10-12%), taxable gains (LTCG 20%, STCG slab rate), fully liquid. Best approach: Maximize NPS first, then VPF for remaining deduction space, then mutual funds.",
        },
        {
          title: "Strategic VPF Contribution Planning",
          body: "For ₹5L salary earner: Mandatory EPF ₹50K/month. Can add ₹12K-15K VPF/month (total ₹62-65K stays under ₹7.5L annual limit). Over 25 years, additional ₹15K VPF adds ~₹80L to corpus (including interest). Especially valuable for those 40-50 years old catching up on retirement planning.",
        },
      ],
      faqs: [
        {
          question: "What is the VPF contribution limit?",
          answer:
            "No fixed limit, but total PF (mandatory + VPF) should not exceed ₹7.5L p.a. (₹62,500/month on ₹5.2L basic). Employer cannot exceed 12% contribution.",
        },
        {
          question:
            "Is VPF withdrawal same as EPF?",
          answer:
            "Partially. Mandatory PF: flexible withdrawal. VPF: stricter rules. Withdraw only at retirement or after 5 years of continuous VPF. Check with your HR/PF office.",
        },
        {
          question:
            "Should I prioritize NPS or VPF?",
          answer:
            "NPS first (₹2.5L deductible, better for tax + higher returns potential). Then VPF (additional ₹5L deductible). Combined: max tax deduction under 80C + retirement savings.",
        },
        {
          question:
            "Can I contribute VPF even if I've already maxed NPS?",
          answer:
            "Yes. NPS is capped at ₹2.5L for 80C. VPF is separate deduction (up to ₹7.5L total PF, but usually ₹3-5L is feasible). Consult CA for your exact limit.",
        },
        {
          question:
            "Is VPF worth it if I can invest in mutual funds?",
          answer:
            "Yes. VPF: guaranteed 8.25%, tax-deductible, safe. MF: higher returns (10-12%), but taxed on gains. VPF is better for 'forced savings' + tax efficiency; MF for growth portion.",
        },
      ],
      relatedSlugs: [
        "retirement/epf-calculator",
        "investment/nps-calculator",
        "retirement/retirement-planning",
      ],
    },
  },

  // 6. EPS Pension Calculator
  {
    slug: "eps-pension-calculator",
    category: "retirement",
    title: "EPS Pension Calculator",
    shortTitle: "EPS Monthly Pension",
    description:
      "Calculate your guaranteed lifetime pension benefit under EPS-95",
    keywords: [
      "eps pension",
      "employee pension scheme",
      "monthly pension",
      "retirement income",
      "social security",
    ],
    inputs: [
      {
        type: "number",
        key: "lastDrawnSalary",
        label: "Last Drawn Monthly Salary (Basic)",
        prefix: "₹",
        min: 10000,
        max: 5000000,
        step: 5000,
        default: 100000,
      },
      {
        type: "number",
        key: "yearsOfService",
        label: "Years of Service (for EPS)",
        suffix: "years",
        min: 10,
        max: 50,
        step: 1,
        default: 30,
      },
    ],
    calculate(v) {
      const salary = v.lastDrawnSalary as number;
      const years = v.yearsOfService as number;

      // EPS-95: Pension = (Salary × Service Years) / 70
      // But capped at min 50% of salary, max 100% of salary
      const pensionNumerator = salary * years;
      let monthlyPension = pensionNumerator / 70;

      // Floor: minimum 50% of last salary
      monthlyPension = Math.max(monthlyPension, salary * 0.5);
      // Ceiling: maximum 100% of last salary
      monthlyPension = Math.min(monthlyPension, salary);

      const annualPension = monthlyPension * 12;

      return {
        lastDrawnSalary: salary,
        yearsOfService: years,
        monthlyPension: Math.round(monthlyPension),
        annualPension: Math.round(annualPension),
        pensionAsPercentage: Math.round(
          (monthlyPension / salary) * 100 * 100
        ) / 100,
      };
    },
    resultFields: [
      {
        key: "monthlyPension",
        label: "Monthly EPS Pension",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "annualPension",
        label: "Annual Pension",
        format: "currency",
      },
      {
        key: "pensionAsPercentage",
        label: "Pension as % of Salary",
        format: "percent",
      },
    ],
    content: {
      whatIs:
        "The Employee Pension Scheme (EPS-95) is a defined-benefit pension program guaranteed by the Government of India, offered to private sector employees covered under EPFO. Unlike market-dependent pensions, EPS guarantees a fixed monthly income for life starting at age 58-60 based on your service history and final salary. From your EPF account, 3.67% is allocated to EPS (separate from your 12% EPF contribution), creating a social security net. The pension formula is (Last Drawn Monthly Salary × Years of Service) / 70, capped at 50-100% of your salary (minimum ₹1,000/month, maximum ₹15,000/month as of 2024). For example, a ₹1 lakh salary employee with 30 years service receives approximately ₹42,857/month pension. EPS is inflation-indexed (nominally) and covers your spouse (50% pension after your death). This calculator shows the reliability of EPS as a retirement income anchor, though typically it covers 40-60% of retirement needs, requiring supplementation from EPF balance and personal savings.",
      sections: [
        {
          title: "Who Gets EPS Pension?",
          body: "Private sector employees covered under EPFO with minimum 10 years of continuous service. If you have 8-9 years, your EPS contributions are refunded (without interest). EPS applies automatically unless you opt out. Government employees have different pension schemes (7CPC, etc.). Self-employed individuals use NPS instead. Contract workers in organizations with 20+ employees can access EPS if registered with EPFO.",
        },
        {
          title: "EPS Contribution & How It Works",
          body: "Your employer contributes 12% to EPF. Of this, 3.67% is deducted and credited to EPS Pension Fund (managed by EPFO). You receive full EPF balance at retirement; EPS 3.67% contribution is invested in government bonds/securities for guaranteed returns (~5-6%). At age 58-60, you can claim pension for life (or choose lumpsum commutation if needed).",
        },
        {
          title: "EPS Pension Formula & Cap",
          body: "Pension = (Last Drawn Salary × Years of Service) / 70. Example: ₹1L salary × 30 years / 70 = ₹42,857/month. Maximum pension capped at ₹15,000/month (as of 2024, indexed annually). If calculation exceeds cap, you get ₹15,000. Minimum pension is ₹1,000/month. Service includes all EPFO-registered employment (transfers between jobs counted).",
        },
        {
          title: "Survivor Benefits & Pension Rules",
          body: "EPS pension is guaranteed for your entire life, indexed for inflation (though at slower rate than CPI). After your death, spouse receives 50% of your pension for life. Children don't receive benefits (unlike NPS). You can commute (take lumpsum) 50% of pension value, but most advisors recommend continuing pension for longevity protection. Pension is taxable income (slab rates apply).",
        },
        {
          title: "EPS Limitations & Integration with Retirement Plans",
          body: "EPS alone is insufficient (₹15,000/month = ₹1.8L/year). Combine with EPF balance (₹30-50L typical) and personal savings. Cannot increase EPS by contributing extra (no flexibility like NPS). Best strategy: Rely on EPS for basic living expenses (bread-and-butter), EPF for emergencies/discretionary spending, and personal mutual funds for lifestyle improvements.",
        },
      ],
      faqs: [
        {
          question:
            "Is EPS pension enough to retire on?",
          answer:
            "Depends on your lifestyle. EPS: typically 50-80% of last salary. If last salary ₹1L, EPS = ₹50-80K/month. For basic living, sufficient. For comfort, needs supplement from EPF/personal savings.",
        },
        {
          question:
            "What if I have only 8 years of service?",
          answer:
            "EPS requires minimum 10 years of service to get pension. Below 10 years: EPS contribution is refunded (without interest). Work 2+ more years to qualify.",
        },
        {
          question:
            "Is EPS pension guaranteed for life?",
          answer:
            "Yes. Pension is guaranteed for life, indexed for inflation (nominally). After death, spouse receives 50% pension for life. Protects against longevity risk.",
        },
        {
          question:
            "Can I increase my EPS pension?",
          answer:
            "No. EPS pension is based on salary at retirement. To maximize: work longer (higher service years) or delay retirement (recalculation at higher salary).",
        },
        {
          question:
            "What happens to my EPF when I opt for EPS?",
          answer:
            "EPF (employee share) remains yours. Employer EPF contribution is partially transferred to EPS. At retirement: you get full EPF balance + monthly EPS pension for life.",
        },
      ],
      relatedSlugs: [
        "retirement/epf-calculator",
        "retirement/retirement-income-calculator",
        "retirement/retirement-corpus-calculator",
      ],
    },
  },

  // 7. Atal Pension Yojana (APY)
  {
    slug: "atal-pension-yojana",
    category: "retirement",
    title: "Atal Pension Yojana Calculator",
    shortTitle: "APY Pension",
    description:
      "Secure fixed retirement income with government-backed APY pension scheme",
    keywords: [
      "apy",
      "atal pension yojana",
      "guaranteed pension",
      "retirement income",
      "government scheme",
    ],
    inputs: [
      {
        type: "number",
        key: "currentAge",
        label: "Current Age",
        suffix: "years",
        min: 18,
        max: 65,
        step: 1,
        default: 30,
      },
      {
        type: "select",
        key: "pensionTier",
        label: "Target Monthly Pension",
        options: [
          { value: "1000", label: "₹1,000/month" },
          { value: "2000", label: "₹2,000/month" },
          { value: "3000", label: "₹3,000/month" },
          { value: "4000", label: "₹4,000/month" },
          { value: "5000", label: "₹5,000/month" },
        ],
        default: "3000",
      },
    ],
    calculate(v) {
      const age = v.currentAge as number;
      const pensionTarget = parseInt(v.pensionTier as string);

      // APY contribution matrix (based on age at entry and target pension)
      // Simplified: contribution varies by age and pension target
      let monthlyContribution = 0;

      if (pensionTarget === 1000) {
        if (age <= 25) monthlyContribution = 42;
        else if (age <= 30) monthlyContribution = 58;
        else if (age <= 35) monthlyContribution = 86;
        else if (age <= 40) monthlyContribution = 129;
        else if (age <= 45) monthlyContribution = 181;
        else if (age <= 50) monthlyContribution = 272;
        else if (age <= 55) monthlyContribution = 454;
      } else {
        monthlyContribution = (pensionTarget / 1000) * monthlyContribution;
      }

      const yearsToRetirement = 60 - age;
      const yearsOfPension = 100 - 60; // Assume life expectancy 100
      const totalContribution = monthlyContribution * 12 * yearsToRetirement;

      return {
        targetPension: Math.round(pensionTarget),
        monthlyContribution: Math.round(monthlyContribution),
        yearsToRetirement: Math.round(yearsToRetirement),
        yearsOfPension: yearsOfPension,
        totalContributionRequired: Math.round(totalContribution),
        governmentCoContribution: Math.round(totalContribution * 0.5),
      };
    },
    resultFields: [
      {
        key: "monthlyContribution",
        label: "Your Monthly Contribution",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "targetPension",
        label: "Guaranteed Monthly Pension",
        format: "currency",
      },
      {
        key: "totalContributionRequired",
        label: "Total You Will Contribute",
        format: "currency",
      },
      {
        key: "governmentCoContribution",
        label: "Government Co-Contribution (50%)",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "The Atal Pension Yojana (APY), launched in 2015 by the Government of India, is a flagship social security scheme providing guaranteed lifelong pension for unorganized sector workers—self-employed individuals, informal workers, and anyone without employer-backed pension schemes. Unlike market-dependent investments, APY offers fixed monthly pension (₹1,000-₹5,000) starting at age 60, guaranteed for life. The scheme's unique feature is government co-contribution: the government matches 50% of your contribution for the first 5 years, significantly accelerating corpus accumulation. For example, a 30-year-old contributing ₹100/month receives ₹50/month government co-contribution for 5 years, then continues solo contributions until age 60, securing ₹3,000/month guaranteed pension. APY is ideal for self-employed individuals (doctors, lawyers, traders), small business owners, and informal workers who lack employer benefits. This calculator shows exactly how much you need to contribute monthly to reach your target pension, factoring in government support and compound growth over decades.",
      sections: [
        {
          title: "Who Should Join APY?",
          body: "Indian citizens aged 18-40 years. Self-employed professionals (doctors, lawyers, CA, consultants). Small business owners and entrepreneurs. Informal sector workers (construction, agriculture). Anyone without employer-sponsored pension. Requires valid bank account and Aadhaar for enrollment. Those above 40 can join (up to 55 years) but with higher contribution requirements. Best suited for those expecting 20+ years until retirement.",
        },
        {
          title: "How Does APY Benefit from Government Co-Contribution?",
          body: "Government matches 50% of your monthly contribution for first 5 years only. Example: Contribute ₹100/month → Government adds ₹50/month for 60 months (₹3,000 total government boost). This co-contribution accelerates corpus building significantly, reducing your personal burden. After 5 years, you contribute solo until age 60. Early joining (age 18-25) maximizes government co-contribution benefit due to compounding.",
        },
        {
          title: "APY Pension Options at Age 60",
          body: "On reaching 60, you receive monthly pension for life (₹1,000-₹5,000 based on tier). Pension is indexed nominally (not fully inflation-protected like CPI). Upon your death, spouse receives 50% pension for life. You can exit and claim lumpsum commutation value (50% of corpus before annuity purchase), but most advisors recommend continuing pension for longevity protection and guaranteed income.",
        },
        {
          title: "APY Limitations & Tax Implications",
          body: "Capped at ₹5,000/month maximum pension (can combine with NPS for higher income). Locked until age 60 (no early withdrawal except death). Monthly contributions are not tax-deductible (unlike NPS 80C benefit). Pension received is taxable as income (slab rates apply). Spouse can continue contributions if member dies before 60; otherwise lumpsum is released.",
        },
        {
          title: "APY vs NPS vs Mutual Funds Strategy",
          body: "APY: Guaranteed, low contribution, ideal for low-middle income. NPS: Tax-deductible, higher potential returns, more flexible. MFs: Highest growth potential, fully liquid, but market-dependent. Optimal strategy for self-employed: Max APY first (guaranteed anchor), then NPS (₹50K/year + ₹2L for 50+), then mutual funds for growth portion.",
        },
      ],
      faqs: [
        {
          question:
            "Who is eligible for APY?",
          answer:
            "Indian citizens aged 18-40 years. No income/education requirement. Self-employed, informal workers, anyone without employer pension eligible. Requires bank account and Aadhaar.",
        },
        {
          question:
            "How much does government contribute?",
          answer:
            "Government co-contributes 50% of your contribution (up to ₹1,000/month) for first 5 years only. If you contribute ₹100/month, govt adds ₹50 for 5 years, then you contribute fully.",
        },
        {
          question:
            "What if I want more than ₹5,000/month pension?",
          answer:
            "APY caps at ₹5,000/month. For higher pension, combine: APY (₹5,000) + NPS (₹2.5L contribution limit) + personal savings/investments.",
        },
        {
          question:
            "Can I withdraw from APY before age 60?",
          answer:
            "No. APY is locked until 60. If you die before 60, spouse can continue contributions and receive pension, or exit and get balance. Non-transferable.",
        },
        {
          question:
            "What if I miss a contribution?",
          answer:
            "You lose government co-contribution for that month. Account becomes inactive. Resume contributions anytime; back payments not required, but co-contribution benefit lost.",
        },
      ],
      relatedSlugs: [
        "retirement/nps-calculator",
        "investment/sip-calculator",
        "retirement/retirement-planning",
      ],
    },
  },

  // 8. National Pension Scheme (NPS - Retirement Planning)
  {
    slug: "national-pension-scheme",
    category: "retirement",
    title: "NPS at Retirement Calculator",
    shortTitle: "NPS Retirement",
    description:
      "Project NPS corpus at 60 with annuity purchase & flexible lump-sum options",
    keywords: [
      "nps retirement",
      "national pension scheme",
      "nps corpus",
      "retirement planning",
      "nps pension",
    ],
    inputs: [
      {
        type: "number",
        key: "currentAge",
        label: "Current Age",
        suffix: "years",
        min: 18,
        max: 65,
        step: 1,
        default: 35,
      },
      {
        type: "number",
        key: "monthlyContribution",
        label: "Monthly NPS Contribution",
        prefix: "₹",
        min: 500,
        max: 500000,
        step: 500,
        default: 10000,
      },
      {
        type: "number",
        key: "annualBonus",
        label: "Annual Extra Contribution",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 10000,
        default: 50000,
      },
      {
        type: "select",
        key: "allocationScheme",
        label: "Asset Allocation",
        options: [
          { value: "e80", label: "E 80% (Aggressive)" },
          { value: "e60", label: "E 60% (Moderate)" },
          { value: "e40", label: "E 40% (Conservative)" },
        ],
        default: "e60",
      },
    ],
    calculate(v) {
      const age = v.currentAge as number;
      const monthly = v.monthlyContribution as number;
      const bonus = v.annualBonus as number;
      const scheme = v.allocationScheme as string;
      const retirementAge = 60;

      let equityReturn = 0.10;
      let debtReturn = 0.065;

      if (scheme === "e80") {
        equityReturn = 0.11;
      } else if (scheme === "e60") {
        equityReturn = 0.10;
      } else {
        equityReturn = 0.09;
      }

      const yearsToRetirement = retirementAge - age;
      const monthlyRate = equityReturn / 12;
      const months = yearsToRetirement * 12;

      let corpus = 0;
      for (let m = 0; m < months; m++) {
        if (m % 12 === 0 && m > 0) {
          corpus += bonus;
          corpus *= 1 + monthlyRate;
        }
        corpus = corpus * (1 + monthlyRate) + monthly;
      }

      // 40% goes to annuity (monthly pension)
      const annuityCorpus = corpus * 0.4;
      const monthlyPension = Math.round(annuityCorpus / 100 * 0.5);

      return {
        corpus: Math.round(corpus),
        yearsToRetirement: yearsToRetirement,
        retirementAge: retirementAge,
        annuityCorpus: Math.round(annuityCorpus),
        withdrawableCorpus: Math.round(corpus * 0.6),
        estimatedMonthlyPension: monthlyPension,
      };
    },
    resultFields: [
      {
        key: "corpus",
        label: "Estimated NPS Corpus at 60",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "estimatedMonthlyPension",
        label: "Estimated Monthly Pension",
        format: "currency",
      },
      {
        key: "withdrawableCorpus",
        label: "Lumpsum Withdrawal (60%)",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "The National Pension Scheme (NPS) Tier-I is a tax-deductible voluntary retirement savings scheme regulated by PFRDA (Pension Fund Regulatory and Development Authority), designed to provide both lump-sum corpus and lifetime pension upon retirement. One of NPS's most attractive features for Indian investors is its tax efficiency: contributions up to ₹50,000 are deductible under Section 80C, with an additional ₹2,00,000 deduction under Section 80CCD(1B) for those above 50 years. At retirement (age 60), your accumulated corpus is split: 40% is mandatorily used to purchase an annuity (providing guaranteed monthly pension for life), and 60% is accessible as lump-sum withdrawal. This calculator projects your corpus based on asset allocation (equity, debt, balanced options), monthly contributions, and bonus/lump-sum investments. For a 35-year-old contributing ₹10,000/month with 10% annual returns, NPS corpus at 60 typically reaches ₹2.5-3 crore, generating both ₹60-80L lump-sum access and ₹15,000-25,000/month pension. NPS is ideal for salaried professionals and self-employed individuals seeking maximum tax benefits while building retirement security.",
      sections: [
        {
          title: "NPS Tier-I vs Tier-II: Which to Choose?",
          body: "Tier-I: Locked until age 60 (except medical emergency, exit after age 55 with restrictions). Tax-deductible contributions (₹50K under 80C + ₹2L under 80CCD(1B)). Mandatory annuity purchase at retirement. Tier-II: Fully flexible, no lock-in, can withdraw anytime. No tax deduction on contributions. Best strategy: Maximize Tier-I (tax-efficient) for retirement core; use Tier-II for medium-term goals if needed.",
        },
        {
          title: "Asset Allocation Choices in NPS",
          body: "Equity-80% (E80): Most aggressive, 11% expected return, suitable for 20+ years. Equity-60% (E60): Balanced, 10% expected return, ideal for mid-career professionals. Equity-40% (E40): Conservative, 9% expected return, recommended within 5-10 years of retirement. Auto Choice: Automatically shifts allocation based on age (young = aggressive, near 60 = conservative). Advisor Choice: Custodian manages allocation for ₹250/month fee.",
        },
        {
          title: "Annuity at Retirement: 40% Rule",
          body: "At age 60, 40% of NPS corpus must purchase an annuity (pension) from IRDA-approved insurers. You choose annuity type: life annuity (pension until death), joint annuity (covers spouse after death), or life-with-term annuity (guaranteed period). Example: ₹1 crore corpus → ₹40 lakh buys annuity (~₹25,000/month pension), ₹60 lakh is lumpsum. Annuity is tax-paid (life insurance component); periodic payments are taxable income.",
        },
        {
          title: "Lump-Sum Withdrawal (60%) Options",
          body: "The 60% withdrawable corpus provides flexibility to cover large expenses, clear debts, or invest further. You can withdraw 60% at once or in installments. Tax: No tax on withdrawal (since it's post-tax NPS contribution + growth, annuity already taxed). This lump-sum, combined with EPF balance and gratuity, forms the emergency/discretionary fund for early retirement years.",
        },
        {
          title: "NPS Advantages for Indian Retirement Planning",
          body: "Maximum tax deduction (₹2.5L annually for 50+). Lower expense ratio (0.009-0.01%, vs MF 0.5-1%). Lifetime pension from annuity (longevity risk protection). Professional fund management across asset classes. Regulatory oversight by PFRDA. Portability across jobs (one NPS account). Solo self-employed can also contribute. Best combined with EPF/EPS (if applicable) and mutual funds for comprehensive retirement strategy.",
        },
      ],
      faqs: [
        {
          question:
            "What's the difference between withdrawal and pension?",
          answer:
            "Lumpsum (60%): you withdraw all at once, invest as you like, but runs out if not managed. Pension (40%): fixed monthly income for life, no investment risk, but less flexible.",
        },
        {
          question:
            "Can I delay NPS pension till 65?",
          answer:
            "Yes. NPS allows withdrawal starting age 60, but you can delay and continue investing until 70. Delaying increases corpus due to extended compounding.",
        },
        {
          question:
            "Is NPS pension better than SWP from mutual funds?",
          answer:
            "Both are good. NPS: guaranteed pension (inflation-adjusted minimally), no market risk, longevity protection. SWP: flexible, taxed as per fund type, market risk. Use both for balance.",
        },
        {
          question:
            "Can my spouse inherit NPS on my death?",
          answer:
            "Partially. Annuity chosen determines spouse benefit. Family pension (50% to spouse, 25% to each child) available. Lumpsum (60%) goes to nominee. Consult NPS office for options.",
        },
        {
          question:
            "What is the best annuity option for NPS?",
          answer:
            "Most choose 'Life Annuity' (pension till death). Some choose 'Joint Annuity' (spouse gets pension after death). Avoid 'Fixed Period' unless specific reason (e.g., age 80+). Consult advisor.",
        },
      ],
      relatedSlugs: [
        "investment/nps-calculator",
        "retirement/retirement-income-calculator",
        "retirement/retirement-corpus-calculator",
      ],
    },
  },

  // 9. Annuity Calculator
  {
    slug: "annuity-calculator",
    category: "retirement",
    title: "Annuity Calculator",
    shortTitle: "Annual Income from Corpus",
    description:
      "Calculate sustainable monthly income from retirement corpus at target return",
    keywords: [
      "annuity",
      "annuity income",
      "retirement income stream",
      "fixed income",
      "pension annuity",
    ],
    inputs: [
      {
        type: "number",
        key: "corpus",
        label: "Retirement Corpus",
        prefix: "₹",
        min: 100000,
        max: 1000000000,
        step: 100000,
        default: 5000000,
      },
      {
        type: "number",
        key: "annualReturn",
        label: "Expected Annual Return",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.5,
        default: 6,
      },
      {
        type: "number",
        key: "yearsToWithdraw",
        label: "Expected Withdrawal Years",
        suffix: "years",
        min: 10,
        max: 60,
        step: 1,
        default: 30,
      },
      {
        type: "radio",
        key: "withdrawalType",
        label: "Withdrawal Type",
        options: [
          { value: "equal", label: "Equal Monthly Withdrawal" },
          { value: "inflation", label: "Inflation-Adjusted Withdrawal" },
        ],
        default: "equal",
      },
    ],
    calculate(v) {
      const corpus = v.corpus as number;
      const returnRate = (v.annualReturn as number) / 100;
      const years = v.yearsToWithdraw as number;
      const withdrawalType = v.withdrawalType as string;

      const monthlyRate = returnRate / 12;
      const months = years * 12;

      let monthlyWithdrawal = 0;
      if (monthlyRate > 0) {
        // PV = PMT × [1 - (1+r)^-n] / r
        monthlyWithdrawal =
          corpus /
          ((1 - Math.pow(1 + monthlyRate, -months)) / monthlyRate);
      } else {
        monthlyWithdrawal = corpus / months;
      }

      const annualWithdrawal = monthlyWithdrawal * 12;
      const withdrawalYield = (annualWithdrawal / corpus) * 100;

      return {
        corpus: Math.round(corpus),
        monthlyWithdrawal: Math.round(monthlyWithdrawal),
        annualWithdrawal: Math.round(annualWithdrawal),
        withdrawalYield: Math.round(withdrawalYield * 100) / 100,
        yearsToWithdraw: years,
        totalWithdrawn: Math.round(monthlyWithdrawal * months),
      };
    },
    resultFields: [
      {
        key: "monthlyWithdrawal",
        label: "Monthly Income",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "annualWithdrawal",
        label: "Annual Income",
        format: "currency",
      },
      {
        key: "withdrawalYield",
        label: "Withdrawal Rate",
        format: "percent",
      },
    ],
    content: {
      whatIs:
        "An annuity is a financial instrument that converts a lump-sum retirement corpus into predictable monthly or annual income for a defined period (or lifetime). Using the present value of annuity formula (PMT = PV / [1 - (1+r)^-n] / r), this calculator shows sustainable withdrawal rates that prevent premature corpus depletion. For Indian retirees, understanding annuity is critical because it bridges the gap between a one-time pension/corpus and lifetime living expenses. The 4% withdrawal rule—withdrawing 4% of initial corpus annually (₹4L from ₹1 crore)—is widely recommended to ensure corpus lasts 30+ years. This calculator applies to both commercial annuities (purchased from insurance companies) and systematic withdrawal plans (SWP) from mutual funds. For a ₹1 crore corpus at 6% expected returns, sustainable monthly income is approximately ₹83,000, ensuring both inflation protection and longevity security. This tool is essential for anyone transitioning from earning to consuming their retirement savings.",
      sections: [
        {
          title: "Annuity vs SWP: Two Income Strategies",
          body: "Commercial Annuity: Buy from insurance company (IRDA-regulated); guarantees fixed income for life or period; no market risk; inflexible; income decreases in real terms (inflation erodes). SWP (Systematic Withdrawal Plan): Mutual fund scheme; withdraw monthly from holdings; flexible; market risk; taxed on gains; can adjust amounts. Hybrid approach: 50% corpus in commercial annuity (secure base), 50% in SWP (flexibility + growth).",
        },
        {
          title: "The 4% Rule: Golden Standard for Safe Withdrawal",
          body: "Academic research (Trinity Study) shows that withdrawing 4% of corpus annually (adjusted for inflation) has 95% success rate over 30 years. Example: ₹1 crore corpus → ₹4 lakh/year (₹33K/month), increasing yearly by inflation (5%). Year 2 = ₹4.2L, Year 3 = ₹4.41L, etc. This rule accounts for market volatility and ensures purchasing power preservation. Conservative retirees use 3%, aggressive use 5%. Most planners recommend 3-4% for Indian context (longer life expectancy, variable market returns).",
        },
        {
          title: "Inflation Impact on Fixed Annuity Income",
          body: "A fixed annuity (₹83,000/month) loses purchasing power yearly. At 5% inflation, that ₹83K becomes ₹79K value in 1 year, ₹75K in 2 years. After 20 years, purchasing power drops 50%. Mitigation: Choose inflation-linked annuity (initial income lower, increases yearly with inflation). Or combine annuity (basic needs) with SWP from equity (inflation hedge). Or plan for corpus growth even in retirement (5-6% returns from balanced portfolio).",
        },
        {
          title: "Types of Annuity Products Available in India",
          body: "Life Annuity: Income until death; no residual value. Joint Life: Covers spouse after death. Life-with-Term: Guarantees period (5/10/15 years), then continues to survivor. Money-Back Annuity: Returns portions of invested amount while paying income. Immediate vs Deferred: Immediate (start now), Deferred (start later at higher rate). IRDA-regulated insurers offer these; compare premiums/returns carefully.",
        },
        {
          title: "Planning Your Annuity Corpus Size",
          body: "Most financial planners recommend: Basic needs (40%) + Comfortable needs (30%) + Discretionary (20%) + Emergency buffer (10%). Use annuity for first two tiers (70% of corpus = ₹70L from ₹1Cr). Remaining ₹30L in SWP for flexibility. If retiring early (before 60), use lower withdrawal rate (3%) for 40+ year horizon. Calculate exact corpus needed before annuity purchase; cannot reverse decision.",
        },
      ],
      faqs: [
        {
          question:
            "What is a safe withdrawal rate from retirement corpus?",
          answer:
            "The 4% rule: withdraw 4% of corpus annually (adjusted for inflation). For ₹1Cr, ₹4L/year (₹33K/month). This is sustainable for 30+ years with market returns.",
        },
        {
          question:
            "Should I buy commercial annuity or use SWP?",
          answer:
            "Annuity: guaranteed income, no market risk, longevity protection, but inflexible. SWP: flexible, taxed efficiently, market risk. Best: 50% annuity + 50% SWP for balance.",
        },
        {
          question:
            "How does inflation affect my annuity income?",
          answer:
            "Fixed annuity doesn't increase with inflation (loses value). Inflation-linked annuity increases yearly, protecting purchasing power. Choose inflation-linked if available, even if initial income is lower.",
        },
        {
          question:
            "Can I change my annuity plan after purchasing?",
          answer:
            "Generally no. Commercial annuity is non-exchangeable. Choose wisely. Consider: life only (max income, risky for spouse), joint annuity (spouse protected), period annuity (returns to estate).",
        },
        {
          question:
            "Is annuity income taxed?",
          answer:
            "Yes. Annuity interest is taxed as income annually. If you earn ₹36L p.a. annuity, fully taxable. Allocate 30-40% for taxes if earning. Some forms offer tax deferral.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-income-calculator",
        "investment/swp-calculator",
        "retirement/retirement-corpus-calculator",
      ],
    },
  },

  // 10. FIRE Calculator
  {
    slug: "fire-calculator",
    category: "retirement",
    title: "FIRE Calculator - Financial Independence, Retire Early",
    shortTitle: "FIRE Planning",
    description:
      "Calculate years to financial independence through disciplined saving & investing",
    keywords: [
      "fire",
      "financial independence retire early",
      "early retirement",
      "aggressive saving",
      "independence planning",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyIncome",
        label: "Monthly Income",
        prefix: "₹",
        min: 25000,
        max: 10000000,
        step: 5000,
        default: 200000,
      },
      {
        type: "number",
        key: "monthlySavings",
        label: "Monthly Savings",
        prefix: "₹",
        min: 5000,
        max: 5000000,
        step: 5000,
        default: 100000,
      },
      {
        type: "number",
        key: "currentCorpus",
        label: "Current Invested Assets",
        prefix: "₹",
        min: 0,
        max: 1000000000,
        step: 100000,
        default: 500000,
      },
      {
        type: "number",
        key: "targetExpense",
        label: "Expected Annual Retirement Expense",
        prefix: "₹",
        min: 100000,
        max: 100000000,
        step: 50000,
        default: 1200000,
      },
      {
        type: "number",
        key: "expectedReturn",
        label: "Expected Annual Return",
        suffix: "% p.a.",
        min: 5,
        max: 20,
        step: 0.5,
        default: 12,
      },
    ],
    calculate(v) {
      const income = v.monthlyIncome as number;
      const savings = v.monthlySavings as number;
      const corpus = v.currentCorpus as number;
      const expense = v.targetExpense as number;
      const returnRate = (v.expectedReturn as number) / 100;

      // Calculate required corpus (using 4% rule)
      const requiredCorpus = expense / 0.04;

      // Years to reach required corpus
      const monthlyRate = returnRate / 12;
      let balance = corpus;
      let months = 0;

      while (
        balance < requiredCorpus &&
        months < 1000
      ) {
        balance = balance * (1 + monthlyRate) + savings;
        months++;
      }

      const years = Math.round((months / 12) * 10) / 10;
      const savingsRate = (savings / income) * 100;

      return {
        requiredCorpus: Math.round(requiredCorpus),
        currentCorpus: Math.round(corpus),
        yearsToFIRE: years,
        savingsRate: Math.round(savingsRate * 10) / 10,
        monthlyShortfall: Math.round(Math.max(0, (expense / 12) - savings)),
        projectedCorpus: Math.round(balance),
      };
    },
    resultFields: [
      {
        key: "yearsToFIRE",
        label: "Years to Financial Independence",
        format: "years",
        isPrimary: true,
      },
      {
        key: "requiredCorpus",
        label: "Corpus Needed for Independence",
        format: "currency",
      },
      {
        key: "savingsRate",
        label: "Your Savings Rate",
        format: "percent",
      },
    ],
    content: {
      whatIs:
        "FIRE (Financial Independence, Retire Early) is a movement gaining significant traction in India where professionals pursue financial freedom through aggressive saving and investing, often targeting retirement in their 40s instead of traditional 60. The concept is mathematically elegant: your corpus grows through two engines (savings + investment returns), and the earlier you achieve a corpus generating 4% annual withdrawal = your annual expenses, the sooner you can retire. For example, if you earn ₹15 lakh/year, save ₹7.5 lakh (50% rate), invest at 12% returns, you need ₹1.875 crore corpus (25 × annual expenses). With compound growth, this typically takes 15-17 years. FIRE requires ruthless expense discipline (living on 50% of income is non-negotiable) and investment discipline (12%+ returns from equity SIPs). This calculator shows the power of savings rate as the primary lever—a 50% saver reaches FI in half the time of a 30% saver. For high-income earners in India (₹15L+), FIRE is increasingly realistic, especially when combined with side income or business ventures.",
      sections: [
        {
          title: "The Two Levers of FIRE: Savings Rate & Returns",
          body: "Savings Rate: Every 1% increase in savings rate cuts FIRE timeline by 0.5-1 year. A 30% saver takes 32 years; 40% saver takes 24 years; 50% saver takes 17 years; 60% saver takes 12 years. Returns: 10% vs 12% annual returns adds 5-8 years to timeline. Aggressive allocation (80% equity) is necessary for FIRE timelines. The combination is powerful: 50% savings rate + 12% returns = FIRE in 15-17 years vs 30% savings + 8% returns = 50+ years.",
        },
        {
          title: "FIRE Feasibility for Different Income Levels in India",
          body: "₹15L+ income: Highly achievable; 50% saving is realistic; 15-17 years to FIRE. ₹10-15L income: Possible but requires discipline; target 40-45% savings; 20-25 years. ₹5-10L income: Longer timeline (30-40 years) but viable with consistency; need supplementary income/side gig. Below ₹5L: FIRE challenging; better to focus on pension schemes (EPF/NPS/APY) than FIRE.",
        },
        {
          title: "FIRE in Practice: Real Expense Needs vs Theory",
          body: "Theory says 4% withdrawal rule; practice requires buffer. If FIRE corpus is ₹1.5 crore at 6% returns, annual income = ₹9 lakh (₹75K/month). Your actual retirement expenses might be ₹1.2L/month (₹14.4L/year). Sequence-of-returns risk is real (market crash post-retirement). Mitigation: 50% corpus in annuity/pension (guaranteed base), 50% in SWP (flexibility). Or plan for 3% withdrawal rate (₹45K from ₹1.5Cr). Conservative approach: 3-year expense buffer in FD.",
        },
        {
          title: "Tax Implications of FIRE in India",
          body: "Capital gains tax applies to SWP from mutual funds (LTCG 20% after 1 year, STCG slab rate). Dividend income taxed slab rate. Interest from FD/bonds fully taxable. NPS annuity partially taxed (life insurance component). Planning: Maximize NPS contributions for tax deduction (₹50K + ₹2L for 50+); LTCG-efficient FIRE relies on holding MFs 1+ years. Corpus of ₹1.5 crore generating ₹9L income/year requires tax planning.",
        },
        {
          title: "FIRE Risks & Mitigation Strategies",
          body: "Market crash risk: Keep 3-5 years expenses in FD/debt. Healthcare inflation: Allocate 15-20% of corpus to health insurance/contingency. Longevity risk: Combine FIRE corpus with pension products (EPS/NPS). Inflation risk: Use equity (20-30% allocation even in FIRE) for purchasing power growth. Career risk: Build skills, maintain professional network, stay employed until 50-55 to avoid 20+ years on passive withdrawals.",
        },
      ],
      faqs: [
        {
          question:
            "What savings rate do I need for FIRE?",
          answer:
            "50%+ is aggressive and achievable with ₹15-20L income. 40%+ is still strong. 30% is moderate. Lower rates extend FIRE timeline. Every 10% increase in savings rate saves ~3-5 years.",
        },
        {
          question:
            "Is FIRE realistic in India?",
          answer:
            "Yes, for high-income earners (₹15L+/year). With 50% savings and 12% returns (equity SIP), FIRE in 15-17 years is achievable. Middle-income (₹5-10L) takes longer but possible with discipline.",
        },
        {
          question:
            "What if I can't save 50% of income?",
          answer:
            "Even 30% savings is powerful. Adds 10-15 years to timeline but still viable. Focus on: (a) increasing income through side gigs, (b) reducing expenses without lifestyle compromise.",
        },
        {
          question:
            "Is 4% withdrawal rule enough in India with inflation?",
          answer:
            "4% rule is conservative globally; in India with 5-6% inflation, more risky. Plan for 3-3.5% withdrawal rate for safety. Or combine pension (EPS/NPS/APY) + SWP.",
        },
        {
          question:
            "What if market crashes after I retire?",
          answer:
            "Sequence-of-returns risk is real. Mitigation: (a) keep 3-5 years expenses in cash/FD, (b) use flexible SWP, (c) supplement with pension income, (d) plan for lower returns (8% instead of 12%).",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-planning",
        "retirement/retirement-corpus-calculator",
        "investment/sip-calculator",
      ],
    },
  },

  // 11-20: Additional retirement tools (simplified versions)
  {
    slug: "early-retirement-calculator",
    category: "retirement",
    title: "Early Retirement Calculator (Before 60)",
    shortTitle: "Early Retirement",
    description:
      "Evaluate early retirement feasibility with longer withdrawal horizons",
    keywords: [
      "early retirement",
      "retire before 60",
      "career break",
      "semi-retirement",
    ],
    inputs: [
      {
        type: "number",
        key: "retirementAge",
        label: "Target Retirement Age",
        suffix: "years",
        min: 40,
        max: 59,
        step: 1,
        default: 50,
      },
      {
        type: "number",
        key: "currentCorpus",
        label: "Current Invested Corpus",
        prefix: "₹",
        min: 100000,
        max: 1000000000,
        step: 100000,
        default: 2000000,
      },
      {
        type: "number",
        key: "monthlyExpense",
        label: "Estimated Monthly Retirement Expense",
        prefix: "₹",
        min: 25000,
        max: 5000000,
        step: 5000,
        default: 100000,
      },
      {
        type: "number",
        key: "yearsToRetirement",
        label: "Years Until Retirement",
        suffix: "years",
        min: 1,
        max: 25,
        step: 1,
        default: 15,
      },
    ],
    calculate(v) {
      const corpus = v.currentCorpus as number;
      const expense = v.monthlyExpense as number;
      const years = v.yearsToRetirement as number;
      const returnRate = 0.10; // 10% assumed return

      // Corpus needed (using 3.5% rule for early retirees)
      const requiredCorpus = (expense * 12) / 0.035;

      // Growth of current corpus
      const futureCorpus = corpus * Math.pow(1 + returnRate, years);

      // Gap (if negative, over-funded)
      const gap = requiredCorpus - futureCorpus;

      return {
        requiredCorpus: Math.round(requiredCorpus),
        futureCorpus: Math.round(futureCorpus),
        gap: Math.round(gap),
        monthlyShortfall: Math.round(
          Math.max(0, (expense * 12) / 0.035) / 12 / 35
        ),
        retirementReady: gap <= 0,
      };
    },
    resultFields: [
      {
        key: "requiredCorpus",
        label: "Corpus Needed for Early Retirement",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "futureCorpus",
        label: "Projected Corpus at Retirement",
        format: "currency",
      },
      { key: "gap", label: "Shortfall/Surplus", format: "currency" },
    ],
    content: {
      whatIs:
        "Early retirement before age 60 is increasingly popular in India, especially among high-income professionals and entrepreneurs, but it demands substantially more planning rigor than traditional retirement at 60-65. The key challenge is sequence-of-returns risk: a 40-year-old retiring at 45 faces 45+ years of withdrawals (vs 30 years for age-60 retirement), requiring a significantly larger corpus and much more conservative withdrawal rates (3-3.5% instead of 4%). Moreover, early retirees cannot access EPF balance (locked until 58-60) or EPS pension (not available before 58), losing critical income anchors. However, early retirement is achievable with: (1) high current corpus (₹2-5 crore minimum), (2) disciplined withdrawal rate (3-3.5%), (3) part-time income/business (₹2-5L/year provides crucial buffer), (4) thorough healthcare planning (critical pre-Medicare coverage), and (5) flexibility to adjust spending if markets crash. This calculator shows the feasibility gap: what corpus you need vs what you'll accumulate, helping you decide between retiring now, working a few more years, or pursuing semi-retirement.",
      sections: [
        {
          title: "Why Early Retirement Requires Higher Corpus",
          body: "Traditional retirement at 60: 30-year horizon, corpus grows initially. Early retirement at 45: 45-50 year horizon, long inflation exposure, sequence risk critical. Rule of thumb: Add 50% more corpus for each 5-year earlier retirement. Retiring at 50 (vs 60) requires ~1.5x corpus; retiring at 45 requires ~2x corpus. Example: ₹1Cr for age-60 retirement needs ₹2Cr for age-45 retirement with same lifestyle.",
        },
        {
          title: "The 3.5% vs 4% Withdrawal Rate Debate",
          body: "Age-60 retirement: 4% withdrawal rate has 95% success (Trinity Study, 30-year horizon). Age-45 retirement: 3% rule recommended for 45+ year safety. Example: ₹2Cr corpus at 3.5% = ₹70L/year (₹5.8L/month). At 3% = ₹60L/year (₹5L/month). The 0.5-1% difference dramatically extends corpus life, critical for early retirees.",
        },
        {
          title: "Early Retirement Income Sources & Gaps",
          body: "EPF: Inaccessible until 58-60 (cannot use as income bridge). EPS: No pension before 58 (critical gap). NPS: Locked until 60 (only 40% can convert to annuity at 60). Solution: Rely on personal mutual funds, real estate rental income, side business income, or part-time work. Most successful early retirees (45-50) earn ₹2-5L/year from consulting/freelance/advisory, reducing corpus withdrawals.",
        },
        {
          title: "Healthcare Planning: Pre-Medicare Challenge",
          body: "Senior citizen mediclaim becomes available at 60+. Before 60, health insurance mandatory and expensive. Budget: ₹50-100K/year premium for family (₹25-50L coverage). Major health event (surgery, critical illness) can cost ₹10-20L. Mitigation: Buy comprehensive health insurance (₹50-100L coverage) before retirement. Allocate 5-10% of corpus to healthcare buffer.",
        },
        {
          title: "Part-Time Income: The Game-Changer",
          body: "Even ₹2-5L/year part-time income (10-15 hours/week consulting/freelance/teaching) extends corpus by 10-15 years. This is why many early retirees don't fully quit; they transition to flexible, passion-driven work. ₹5L/year supplementary income allows 3.5% withdrawal rate instead of 3%, significantly improving lifestyle sustainability. Professional skills (tech, finance, law, medical) command ₹3-5L/year consulting fees.",
        },
      ],
      faqs: [
        {
          question:
            "Can I retire at 45 with ₹2Cr corpus?",
          answer:
            "Depends on expenses. ₹2Cr at 3.5% withdrawal = ₹70L/year (₹5.8L/month). If your lifestyle needs ₹2-3L/month, yes. Below that, yes with comfort.",
        },
        {
          question:
            "What about healthcare before Medicare age?",
          answer:
            "Health insurance mandatory. Budget ₹50-100K/year for premium (family). Major illness coverage (₹30-50L) recommended. Include in corpus needs.",
        },
        {
          question:
            "Should I plan part-time work in early retirement?",
          answer:
            "Yes! Part-time income (₹2-5L/year) extends corpus significantly and keeps you engaged. Even 10-15 hours/week consulting/freelance is substantial.",
        },
        {
          question:
            "When can I access EPF/NPS before 60?",
          answer:
            "EPF: limited withdrawal after 7 years. NPS: not accessible before 60 (except life-threatening illness rare cases). Plan accordingly; don't rely on locked funds.",
        },
        {
          question:
            "Is early retirement sustainable?",
          answer:
            "Yes, but requires: (a) higher corpus (3x normal), (b) 3-3.5% withdrawal rate (vs 4%), (c) healthcare plan, (d) flexibility (part-time work, expense reduction if needed).",
        },
      ],
      relatedSlugs: [
        "retirement/fire-calculator",
        "retirement/retirement-corpus-calculator",
        "investment/sip-calculator",
      ],
    },
  },

  {
    slug: "pension-calculator",
    category: "retirement",
    title: "Pension Calculator",
    shortTitle: "Pension Estimation",
    description:
      "Combine all pension sources to estimate total retirement monthly income",
    keywords: [
      "pension",
      "total pension income",
      "retirement income",
      "pension sources",
    ],
    inputs: [
      {
        type: "number",
        key: "epsPension",
        label: "Expected EPS Pension",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 5000,
        default: 50000,
      },
      {
        type: "number",
        key: "npsPension",
        label: "NPS Monthly Pension (40% annuity)",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 5000,
        default: 20000,
      },
      {
        type: "number",
        key: "socialSecurityPension",
        label: "Social Security/APY Pension",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 1000,
        default: 5000,
      },
      {
        type: "number",
        key: "swpIncome",
        label: "SWP Income from Personal Investments",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 5000,
        default: 30000,
      },
      {
        type: "number",
        key: "rentIncome",
        label: "Rental Income (if any)",
        prefix: "₹",
        min: 0,
        max: 500000,
        step: 5000,
        default: 0,
      },
    ],
    calculate(v) {
      const eps = (v.epsPension as number) || 0;
      const nps = (v.npsPension as number) || 0;
      const social = (v.socialSecurityPension as number) || 0;
      const swp = (v.swpIncome as number) || 0;
      const rent = (v.rentIncome as number) || 0;

      const totalMonthly = eps + nps + social + swp + rent;
      const totalAnnual = totalMonthly * 12;

      // Estimate tax (assuming 20% average tax)
      const estimatedTax = totalAnnual * 0.2;
      const afterTaxMonthly = Math.round((totalAnnual - estimatedTax) / 12);

      return {
        epsPension: Math.round(eps),
        npsPension: Math.round(nps),
        socialSecurityPension: Math.round(social),
        swpIncome: Math.round(swp),
        rentIncome: Math.round(rent),
        totalMonthlyGross: Math.round(totalMonthly),
        totalAnnualGross: Math.round(totalAnnual),
        estimatedTaxes: Math.round(estimatedTax),
        totalMonthlyAfterTax: afterTaxMonthly,
      };
    },
    resultFields: [
      {
        key: "totalMonthlyGross",
        label: "Total Monthly Income (Gross)",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalMonthlyAfterTax",
        label: "After-Tax Monthly Income",
        format: "currency",
      },
      {
        key: "estimatedTaxes",
        label: "Estimated Annual Taxes",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "Comprehensive retirement income planning requires aggregating all pension and income sources into a single monthly figure to assess lifestyle sustainability. For Indian retirees, this includes EPS-95 pension (₹10-15K/month guaranteed), NPS annuity payout (40% of corpus converted at 60), APY or social security pensions (if applicable), systematic withdrawal plan (SWP) from mutual funds, rental income from properties, and any consulting/part-time income. This calculator consolidates these streams, showing your total gross monthly income, estimated tax burden (using typical 20% rate), and net after-tax income—the figure actually available for spending. A common misconception is that retirees live on pension alone; most successful retirees rely on diversified income: pension (40-50% for security), SWP/investments (30-40% for flexibility), and passive income (10-20% from property/business). This calculator helps you visualize if your aggregated income matches retirement expenses, and identifies gaps requiring post-retirement adjustments.",
      sections: [
        {
          title: "Components of Retirement Income in India",
          body: "EPS-95 Pension: Guaranteed, life-long, ₹10-15K/month (capped). NPS Annuity: 40% corpus converted to pension at 60 (₹15-30K/month typical). APY Pension: ₹1-5K/month government scheme. SWP from Mutual Funds: Flexible, market-dependent, typically 5-7% annual from ₹50L-₹1Cr corpus (₹20-60K/month). Rental Income: Property/flat rental minus maintenance, taxes, vacancies (₹30-100K/month net). Part-Time Income: Consulting, advisory, teaching (₹10-50K/month).",
        },
        {
          title: "Assessing Retirement Income Adequacy",
          body: "Basic lifestyle (₹50-75K/month): Covered by EPS + minimal SWP. Comfortable lifestyle (₹1-1.5L/month): EPS + NPS annuity + SWP. Luxury lifestyle (₹2L+/month): Requires substantial corpus/passive income. Guideline: Your retirement income should be 70-80% of pre-retirement expense (adjusted for inflation, no kids education, no house EMI). Shortfalls require either: more corpus accumulation, lower retirement expenses, later retirement age, or supplementary income.",
        },
        {
          title: "Tax Impact on Retirement Income",
          body: "EPS Pension: Fully taxable (slab rates apply). NPS Pension: Partially taxed (life insurance component exempt). SWP from MF: LTCG taxed 20% (if held >1 year); STCG taxed slab rate. Rental Income: Net of 30% standard deduction; remaining taxed slab rate. Total tax on ₹1.5L/month gross (~₹18L/year) typically 15-20% effective rate. Plan to have 15-20% tax buffer in income estimates.",
        },
        {
          title: "Strategic Ordering of Withdrawal (Tax Efficiency)",
          body: "Tax-efficient withdrawal order: (1) EPS pension (slab taxed), (2) Rental income (30% deduction), (3) SWP from PPF (interest only, low tax if held post-5 years), (4) SWP from MF (LTCG 20%), (5) NPS withdrawal (post-annuity purchase). This ordering minimizes overall tax burden. Consult CA for personalized strategy based on income slab.",
        },
        {
          title: "Income Gaps & Mitigation Strategies",
          body: "If aggregated income falls short: (a) Reduce retirement expenses (live in cheaper city, downsize property), (b) Extend working years (1 more year increases corpus 8-10%), (c) Optimize SWP timing (delay withdrawals 2-3 years post-retirement), (d) Pursue part-time income (₹2-5L/year supplementary), (e) Monetize assets (sell lesser-used property, downsize home). Most flexible approach: Hybrid retirement (semi-work until 65, ramping SWP gradually).",
        },
      ],
      faqs: [
        {
          question:
            "What is considered reasonable monthly retirement income?",
          answer:
            "Varies by lifestyle. Basic: ₹50-75K/month. Comfortable: ₹1-1.5L/month. Luxury: ₹2L+/month. Assess your pre-retirement expense and factor 80-90% of that.",
        },
        {
          question:
            "Should I include rental income in my pension calculation?",
          answer:
            "Yes, but accounting for maintenance, taxes, and vacancies. If renting a flat for ₹50K/month, assume ₹35-40K net (after 20-30% costs/vacancy).",
        },
        {
          question:
            "How do I minimize taxes on pension income?",
          answer:
            "Utilize Section 80TTA (bank interest up to ₹10K deduction), 80TTC (insurance premiums), medical insurance deductions. Consult CA for rebate u/s 87A if income <₹5L.",
        },
        {
          question:
            "What if my pension income is below my expenses?",
          answer:
            "Supplement with: (a) SWP from personal savings, (b) part-time work, (c) reduce expenses, (d) tap home equity (loan/sale). Most retirees supplement 20-30% from personal corpus.",
        },
        {
          question:
            "Is pension income enough or should I work part-time?",
          answer:
            "Depends on corpus and longevity. If pension = 80%+ of expenses, consider part-time work optional (for engagement). If 50-60%, part-time work recommended.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-income-calculator",
        "retirement/annuity-calculator",
        "investment/swp-calculator",
      ],
    },
  },

  {
    slug: "superannuation-calculator",
    category: "retirement",
    title: "Superannuation Calculator",
    shortTitle: "Superannuation Fund",
    description:
      "Project superannuation fund maturity with employer contributions & returns",
    keywords: [
      "superannuation",
      "gratuity alternative",
      "employer fund",
      "retirement benefits",
    ],
    inputs: [
      {
        type: "number",
        key: "employerContribution",
        label: "Annual Employer Superannuation Contribution",
        prefix: "₹",
        min: 10000,
        max: 10000000,
        step: 50000,
        default: 500000,
      },
      {
        type: "number",
        key: "yearsOfService",
        label: "Years of Service",
        suffix: "years",
        min: 1,
        max: 50,
        step: 1,
        default: 25,
      },
      {
        type: "number",
        key: "returnRate",
        label: "Fund Investment Return Rate",
        suffix: "% p.a.",
        min: 5,
        max: 12,
        step: 0.5,
        default: 8,
      },
    ],
    calculate(v) {
      const contribution = v.employerContribution as number;
      const years = v.yearsOfService as number;
      const returnRate = (v.returnRate as number) / 100;

      const maturityAmount =
        contribution *
        ((Math.pow(1 + returnRate, years) - 1) / returnRate) *
        (1 + returnRate);

      const totalContribution = contribution * years;
      const gains = maturityAmount - totalContribution;

      return {
        maturityAmount: Math.round(maturityAmount),
        totalContribution: Math.round(totalContribution),
        gains: Math.round(gains),
        yearsOfService: years,
        employerContribution: Math.round(contribution),
      };
    },
    resultFields: [
      {
        key: "maturityAmount",
        label: "Superannuation Fund at Retirement",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "totalContribution",
        label: "Total Contributions",
        format: "currency",
      },
      { key: "gains", label: "Investment Gains", format: "currency" },
    ],
    content: {
      whatIs:
        "Superannuation is a structured employer-funded retirement benefit scheme, regulated by PFRDA and the Superannuation (Management) Rules, common in banking, insurance, and multinational corporations. Unlike gratuity (fixed formula: 15×salary×years/26), superannuation is market-linked and can accumulate substantially over 25-30 years of service. An employer contributing ₹5 lakh annually for 25 years, compounded at 8% returns, yields approximately ₹3-4 crore—significantly exceeding gratuity caps of ₹20 lakh. Superannuation is highly tax-efficient: employer contributions are tax-deductible, investment gains within the fund are tax-free, and withdrawal at retirement is partly taxable. Some organizations offer both gratuity (minimum statutory benefit) and superannuation (extra retirement cushion). This calculator projects your superannuation maturity, helping you include this asset in retirement planning and understand the true value of employer retirement benefits often overlooked by employees.",
      sections: [
        {
          title: "How Superannuation Works in Indian Context",
          body: "Employer establishes superannuation fund (often through trustees or professional fund managers). Contributes fixed amount annually (₹3-10L typical for ₹10L+ salary earners). Employee funds employer's contribution, which grows tax-free. Upon retirement/separation, employee receives lumpsum maturity amount. Some funds offer option to receive annuity (monthly pension). Funds are regulated; employer cannot redirect contributions.",
        },
        {
          title: "Superannuation vs Gratuity: Key Differences",
          body: "Gratuity: Fixed formula, capped at ₹20L, statutory right after 5 years. Superannuation: Market-linked, no cap, accumulates over time, employer discretionary (but common benefit). Superannuation usually larger (often ₹30-100L for 25+ years service with 8%+ returns). Tax treatment similar. Smart approach: Verify if your org offers both; if yes, superannuation is 2-5x gratuity value.",
        },
        {
          title: "Tax Treatment of Superannuation",
          body: "Employer Contribution: Deductible to company (not taxed in employee's hand). Investment Gains: Fully tax-free within fund (huge advantage). Withdrawal at Retirement: Partially taxable. If accumulated over 25+ years, typically 50% is tax-free (contribution component) and 50% taxable (gains). Effective tax rate on ₹50L superannuation typically 10-20%.",
        },
        {
          title: "When Can You Access Superannuation?",
          body: "Full Access: At retirement or separation from employment (resignation, termination). Partial Access: Limited medical emergency, housing loan, education (varies by fund rules). Early Separation: Check fund deed; some allow partial withdrawal pre-retirement. Pre-retirement death: Corpus passed to nominee. Cannot touch superannuation like EPF (no mid-service withdrawals for routine needs).",
        },
        {
          title: "Superannuation in Overall Retirement Strategy",
          body: "Superannuation is bonus retirement asset (gratuity-like, but larger and more reliable). Include in total corpus planning: EPF (₹30-50L) + Gratuity (₹15-20L) + Superannuation (₹30-100L) + Personal Savings = Total Corpus. For ₹1Cr retirement need, superannuation might cover 30-50% alone. Strategic advantage: Employer-funded, hands-off growth, tax-efficient—requires zero personal discipline unlike SIPs/investments.",
        },
      ],
      faqs: [
        {
          question:
            "Is superannuation taxed?",
          answer:
            "Partially. Employer contribution is tax-deductible to employer. Employee doesn't pay tax on contribution. Investment gains inside fund are tax-free. Withdrawal at retirement is partly taxable.",
        },
        {
          question:
            "Can I withdraw superannuation before retirement?",
          answer:
            "Varies by plan. Generally allowed for: medical emergency, housing loan, education. Full withdrawal only at separation/retirement. Check your plan rules with employer.",
        },
        {
          question:
            "How is superannuation different from gratuity?",
          answer:
            "Gratuity: fixed formula (15×salary×years)/26, max ₹20L. Superannuation: market-linked, employer-funded, no cap. Superannuation usually larger if investments do well.",
        },
        {
          question:
            "What if employer offers both gratuity and superannuation?",
          answer:
            "Most companies offer one or the other, not both. Some offer: gratuity (statutory minimum) + superannuation (extra benefit). Ask HR which applies to you.",
        },
        {
          question:
            "Should I count superannuation in retirement planning?",
          answer:
            "Yes, definitely. Superannuation is a significant retirement asset (often ₹20-100L depending on salary/years). Include in total retirement corpus and plan withdrawals carefully.",
        },
      ],
      relatedSlugs: [
        "retirement/gratuity-calculator",
        "retirement/retirement-corpus-calculator",
        "retirement/epf-calculator",
      ],
    },
  },

  {
    slug: "commuted-pension",
    category: "retirement",
    title: "Commuted Pension Calculator",
    shortTitle: "Lumpsum vs Pension",
    description:
      "Compare financial impact of pension commutation vs keeping monthly income",
    keywords: [
      "commuted pension",
      "lumpsum vs pension",
      "pension options",
      "retirement income choice",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyPension",
        label: "Offered Monthly Pension",
        prefix: "₹",
        min: 5000,
        max: 500000,
        step: 5000,
        default: 50000,
      },
      {
        type: "number",
        key: "commutablePercent",
        label: "Commutable as Lumpsum",
        suffix: "%",
        min: 25,
        max: 100,
        step: 5,
        default: 50,
      },
      {
        type: "number",
        key: "lifeExpectancy",
        label: "Your Life Expectancy",
        suffix: "years",
        min: 65,
        max: 100,
        step: 1,
        default: 85,
      },
      {
        type: "number",
        key: "currentAge",
        label: "Your Current Age",
        suffix: "years",
        min: 50,
        max: 95,
        step: 1,
        default: 60,
      },
    ],
    calculate(v) {
      const pension = v.monthlyPension as number;
      const commutable = (v.commutablePercent as number) / 100;
      const lifeExpectancy = v.lifeExpectancy as number;
      const age = v.currentAge as number;

      const lumpsumAvailable = pension * 180 * commutable; // Approx formula
      const retainedPension = pension * (1 - commutable);

      const yearsOfPension = lifeExpectancy - age;
      const totalPensionPayment = retainedPension * 12 * yearsOfPension;
      const totalBenefitWithPension =
        lumpsumAvailable + totalPensionPayment;

      return {
        lumpsumOption: Math.round(lumpsumAvailable),
        retainedPension: Math.round(retainedPension),
        totalPensionPayment: Math.round(totalPensionPayment),
        totalBenefitWithPension: Math.round(totalBenefitWithPension),
        yearsOfPension: yearsOfPension,
      };
    },
    resultFields: [
      {
        key: "lumpsumOption",
        label: "Lumpsum Option Available",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "retainedPension",
        label: "Retained Monthly Pension",
        format: "currency",
      },
      {
        key: "totalBenefitWithPension",
        label: "Total Benefit (Lumpsum + Pension)",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "Pension commutation is a critical decision at retirement where you elect to convert a portion (typically 25-50%) of your monthly pension into a lumpsum amount, paid immediately, while retaining the remaining pension for life. For government/quasi-government employees with EPS or defined-benefit pensions, this choice is permanent and impacts lifetime income. The decision hinges on your confidence in investment returns: if you believe you can generate 10%+ annual returns through equity investing (and have 20-30 year horizon), commutation is financially superior. However, if you prioritize guaranteed income and peace of mind (especially post-65), retaining the full pension is safer. This calculator uses actuarial formulas to show lumpsum equivalents: a ₹50,000/month pension typically commutes to ₹75-100 lakh (varies by age, interest rates, and mortality tables). Most financial advisors recommend a hybrid approach: commute 50% (get both growth opportunity and guaranteed income) rather than going all-in on either option.",
      sections: [
        {
          title: "Understanding Pension Commutation Rules in India",
          body: "Government Employees: Can commute up to 50% of pension (through authorized insurers). Calculation: Lumpsum = 12.5 × Monthly Pension commutable portion (approx; varies by age/interest tables). EPS Pensioners: Up to 50% of EPS pension can be commuted. Private Sector Pensions (NPS): 40% mandatory annuity, 60% lumpsum—different from traditional commutation. Commutation is irrevocable; once done, cannot be reversed. Choose wisely.",
        },
        {
          title: "The Math: Lumpsum vs Lifetime Pension Payments",
          body: "Example: ₹50K monthly pension (₹6L/year). Full retention: ₹6L/year × 25 years (to 85) = ₹1.5 crore total. Commute 50% (₹25K/month): Get ₹90L lumpsum + ₹25K/month (₹3L/year × 25 = ₹75L) = ₹1.65 crore total. If lumpsum is invested at 10% returns, ₹90L grows to ₹2.3 crore in 25 years. Commutation wins if you can achieve 8%+ returns.",
        },
        {
          title: "Risk Profile: Guaranteed Income vs Market Risk",
          body: "Pension Route: Guaranteed ₹50K/month for life (inflation only nominal). Zero market risk. Longevity risk eliminated. Commutation Route: ₹90L lumpsum + ₹25K pension. If invested at 10%, ₹50K/month sustainable. But market crash within 5 years post-retirement reduces corpus (sequence-of-returns risk). Requires discipline not to over-spend. Risk tolerance critical.",
        },
        {
          title: "Tax Implications of Commutation",
          body: "Commuted Lumpsum: Tax-free up to ₹5 lakh (or 1/3 of commuted value, whichever lower; rules vary). Excess is taxable as income in year of receipt. Retained Pension: Fully taxable as income (slab rate). Investment Returns on Commuted Corpus: LTCG taxed 20% (MF holdings >1 year), interest taxed fully. Net effect: Commutation usually tax-efficient (lumpsum partially exempt) vs full pension (fully taxed).",
        },
        {
          title: "Who Should Commute vs Retain Pension?",
          body: "Commute if: High investment confidence (12%+ expected returns), Age <65 (longer investment horizon), Good health (longevity expected), Want flexibility for inheritance. Retain Pension if: Age >70, Prefer guaranteed income, Poor health/family history, Want zero market risk, Are risk-averse. Hybrid approach: Commute 50% (balance growth + security), retain 50% (guaranteed base).",
        },
      ],
      faqs: [
        {
          question:
            "Should I take commuted pension or keep monthly pension?",
          answer:
            "If you're good at investing (12%+ returns), commute. If you want guaranteed income/peace of mind, retain pension. Most retirees: commute 50% (get both benefits).",
        },
        {
          question:
            "How much lumpsum do I get if I commute 50% pension?",
          answer:
            "Rough formula: ₹150-200 × monthly pension = lumpsum (varies by age, interest rate, life tables). E.g., ₹50K pension → ₹75-100L lumpsum.",
        },
        {
          question:
            "What if I live longer than expected?",
          answer:
            "If you keep pension: no risk (guaranteed for life). If you commute and invest: corpus risk (may deplete). Pension provides longevity protection.",
        },
        {
          question:
            "Can I change my mind after commuting?",
          answer:
            "No. Commutation is permanent. Choose carefully. If uncertain, keep 50% pension (safety) and commute 50% (growth).",
        },
        {
          question:
            "How is commuted lumpsum taxed?",
          answer:
            "Tax-free up to ₹5L (or calculated as per IT Act). Excess taxed as income in that year. Consult CA for exact calculation based on your commutation.",
        },
      ],
      relatedSlugs: [
        "retirement/pension-calculator",
        "retirement/annuity-calculator",
        "investment/swp-calculator",
      ],
    },
  },

  {
    slug: "family-pension-calculator",
    category: "retirement",
    title: "Family Pension Calculator",
    shortTitle: "Family Pension",
    description:
      "Estimate family pension benefits for spouse and children post-retirement",
    keywords: [
      "family pension",
      "survivor pension",
      "dependent pension",
      "death benefits",
    ],
    inputs: [
      {
        type: "number",
        key: "monthlyPension",
        label: "Employee's Monthly Pension",
        prefix: "₹",
        min: 5000,
        max: 500000,
        step: 5000,
        default: 50000,
      },
      {
        type: "select",
        key: "pensionType",
        label: "Pension Scheme",
        options: [
          { value: "eps", label: "EPS Pension" },
          { value: "government", label: "Government Pension" },
          { value: "cpse", label: "CPSE Pension" },
        ],
        default: "eps",
      },
    ],
    calculate(v) {
      const pension = v.monthlyPension as number;
      const scheme = v.pensionType as string;

      let spousePension = 0;
      let childPension = 0;

      if (scheme === "eps") {
        spousePension = pension * 0.5;
        childPension = pension * 0.25; // per child
      } else if (scheme === "government") {
        spousePension = pension * 0.5;
        childPension = pension * 0.25; // per child
      } else {
        spousePension = pension * 0.5;
        childPension = pension * 0.25; // per child
      }

      const familyPensionMonthly = spousePension + childPension;

      return {
        spousePension: Math.round(spousePension),
        childPension: Math.round(childPension),
        totalFamilyPension: Math.round(familyPensionMonthly),
        spouseAnnual: Math.round(spousePension * 12),
      };
    },
    resultFields: [
      {
        key: "spousePension",
        label: "Spouse's Monthly Pension",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "childPension",
        label: "Per Child Monthly Pension",
        format: "currency",
      },
      {
        key: "totalFamilyPension",
        label: "Total Family Pension",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "Family pension is a crucial social security safeguard in India's defined-benefit pension systems (EPS, Government, CPSE), designed to protect dependents (spouse and minor/unmarried children) from financial hardship after the pensioner's death. Unlike life insurance (which requires active premium payments), family pension is an automatic entitlement provided by government/employers under pension schemes. Typically, the surviving spouse receives 50% of the pensioner's monthly pension for life (or until remarriage), and each eligible child receives 25% monthly until age 25 or marriage (whichever earlier). For a pensioner with ₹40,000/month pension, the spouse receives ₹20,000/month for life—substantial security for many middle-class families. This calculator reveals the total family pension payable, highlighting why employee pension schemes (even with their perceived lower returns) are invaluable for financial security. Family pension is partially tax-free and complements life insurance, ensuring comprehensive dependent protection.",
      sections: [
        {
          title: "Who Qualifies for Family Pension Benefits?",
          body: "Surviving Spouse: Entitled to 50% of pensioner's pension for life (or until remarriage, varying by scheme). Dependent Children: Each child gets 25% monthly pension until age 25 (if student/disabled), or upon marriage (if female). Widowed Mother: In some government schemes, mother gets pension if no spouse/children. Dependent Father: Rare, only if entirely dependent (disabled/destitute). Legitimacy & Documentation: Must prove marriage certificate, children birth certificates. Schemes vary; verify with employer/pension authority.",
        },
        {
          title: "Family Pension Amounts: Calculation & Examples",
          body: "EPS Pensioner with ₹40K/month: Spouse gets ₹20K/month (₹2.4L/year) for life. Plus, 1-2 children get ₹10K each (~₹2.4-4.8L/year combined). Total family coverage: ₹4.8-7.2L/year. Adequate for basic needs (rent, utilities, food) but not luxury. Government Pensioner: Similar structure, often with better indexation (inflation adjustment). Most middle-class family pensions: ₹15-30K/month total family benefit sufficient for modest living.",
        },
        {
          title: "Family Pension vs Life Insurance Protection",
          body: "Family Pension: Guaranteed, no cost (employer-funded), inflation-indexed (nominally). Life Insurance: Requires premiums, flexible coverage, market-dependent. Optimal Strategy: Rely on family pension as base (guaranteed), supplement with term life insurance (₹50-100L) for additional child education/home security. Combined approach provides comprehensive family protection.",
        },
        {
          title: "Loss of Family Pension: Key Triggers",
          body: "Spouse Remarriage: Most schemes terminate pension upon remarriage (some exceptions exist). Child Marriage: Female children lose pension upon marriage. Child Age 25+: Children ineligible unless disabled/pursuing full-time education. Remarriage Documentation: Spouse must formally notify employer of remarriage to avoid overpayment recovery.",
        },
        {
          title: "Tax & Financial Planning Around Family Pension",
          body: "Family Pension Income: Partially tax-exempt (portion attributable to employee contribution is tax-free). Net taxable portion: typically 40-50% of pension received. Spouse receiving ₹20K/month (~₹2.4L/year): Minimal tax if no other income. Children's pension: Tax-free if no separate income. Financial planning: Widow should coordinate family pension with other income (rental, investment returns) to optimize tax position.",
        },
      ],
      faqs: [
        {
          question:
            "Who is eligible for family pension?",
          answer:
            "Spouse (lifelong or till remarriage). Children (usually till age 25, or lifelong if disabled). Parents (in some schemes) if no spouse/children. Eligibility varies by employer/scheme.",
        },
        {
          question:
            "Is family pension enough for living expenses?",
          answer:
            "Typically 50-75% of pensioner's expense level. If pensioner's lifestyle was ₹1L/month, spouse/family gets ₹50K+. May need supplement from personal savings/insurance.",
        },
        {
          question:
            "What if there are multiple children?",
          answer:
            "Each child gets 25% pension (usually max 3 children covered). If 4+ children, amount is split proportionally. Check your scheme rules.",
        },
        {
          question:
            "What if spouse remarries after pension-holder's death?",
          answer:
            "In most schemes, spouse loses family pension upon remarriage. Check your employer's rules. Some schemes continue payment even after remarriage (rare).",
        },
        {
          question:
            "Is family pension taxed?",
          answer:
            "Generally tax-free (at least portion is). Consult CA for exact tax treatment under your scheme. Most family pensions have some tax exemption.",
        },
      ],
      relatedSlugs: [
        "retirement/pension-calculator",
        "retirement/gratuity-calculator",
        "insurance/life-insurance-calculator",
      ],
    },
  },

  {
    slug: "retirement-income-calculator",
    category: "retirement",
    title: "Retirement Income Calculator",
    shortTitle: "Monthly Retirement Income",
    description:
      "Find safe monthly income from corpus without risk of premature depletion",
    keywords: [
      "retirement income",
      "monthly income",
      "sustainable withdrawal",
      "retirement lifestyle",
    ],
    inputs: [
      {
        type: "number",
        key: "totalCorpus",
        label: "Total Retirement Corpus",
        prefix: "₹",
        min: 100000,
        max: 1000000000,
        step: 100000,
        default: 5000000,
      },
      {
        type: "number",
        key: "expectedReturn",
        label: "Expected Annual Return in Retirement",
        suffix: "% p.a.",
        min: 1,
        max: 15,
        step: 0.5,
        default: 6,
      },
      {
        type: "number",
        key: "withdrawalYears",
        label: "Expected Withdrawal Years",
        suffix: "years",
        min: 10,
        max: 60,
        step: 1,
        default: 30,
      },
      {
        type: "radio",
        key: "inflationAdjustment",
        label: "Adjustment for Inflation",
        options: [
          { value: "none", label: "Fixed Withdrawal (No Inflation Adjustment)" },
          { value: "inflation", label: "Inflation-Adjusted (Increases 5% yearly)" },
        ],
        default: "none",
      },
    ],
    calculate(v) {
      const corpus = v.totalCorpus as number;
      const returnRate = (v.expectedReturn as number) / 100;
      const years = v.withdrawalYears as number;
      const adjustment = v.inflationAdjustment as string;

      const monthlyRate = returnRate / 12;
      const months = years * 12;

      let monthlyIncome = 0;
      if (monthlyRate > 0) {
        monthlyIncome =
          corpus /
          ((1 - Math.pow(1 + monthlyRate, -months)) / monthlyRate);
      } else {
        monthlyIncome = corpus / months;
      }

      const annualIncome = monthlyIncome * 12;
      const withdrawalRate = (annualIncome / corpus) * 100;

      return {
        totalCorpus: Math.round(corpus),
        monthlyIncome: Math.round(monthlyIncome),
        annualIncome: Math.round(annualIncome),
        withdrawalRate: Math.round(withdrawalRate * 100) / 100,
        withdrawalYears: years,
        withdrawalSustainability: withdrawalRate <= 4.5 ? "Sustainable" : "Aggressive",
      };
    },
    resultFields: [
      {
        key: "monthlyIncome",
        label: "Monthly Sustainable Income",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "annualIncome",
        label: "Annual Income",
        format: "currency",
      },
      {
        key: "withdrawalRate",
        label: "Withdrawal Rate",
        format: "percent",
      },
    ],
    content: {
      whatIs:
        "This retirement income calculator is the mirror image of the retirement corpus calculator: while corpus planning asks 'How much do I need?', this calculator answers 'How much can I safely spend?'. Given a fixed retirement corpus, it calculates the maximum sustainable monthly withdrawal using the present value of annuity formula, accounting for expected investment returns and withdrawal duration. For Indian retirees, this is critical because it prevents the existential retirement risk: spending too aggressively and running out of money at 85-90. The 4% rule—withdrawing 4% of initial corpus annually—has proven 95% safe over 30-year retirement horizons in western markets; in India with higher inflation (5-6%), the recommended rate is 3-3.5%. For ₹1 crore corpus at 6% returns over 30 years, sustainable monthly income is ₹83,000. This calculator helps you match your retirement corpus to lifestyle needs: if your corpus generates ₹50,000/month but you need ₹100,000, it signals either larger corpus requirement or lifestyle adjustment before retirement.",
      sections: [
        {
          title: "The 4% Rule: Golden Benchmark for Safe Withdrawal",
          body: "Academic research (Trinity Study, Morningstar) shows 4% annual withdrawal (adjusted for inflation) has 95% success rate over 30-year periods. Formula: Year 1 withdrawal = 4% × Corpus; Year 2 = Year 1 × (1 + inflation); Year 3 = Year 2 × (1 + inflation), etc. Example: ₹1Cr corpus → ₹4L first year (₹33K/month), ₹4.2L second year (₹35K/month with 5% inflation), continuing safely for 30 years. Conservative approach for India: Use 3% rule (₹30K/month from ₹1Cr first year); more sustainable given inflation volatility.",
        },
        {
          title: "Withdrawal Rates by Retirement Age & Risk Tolerance",
          body: "Age 45-50 (45+ year horizon): 3% rate maximum (low sequence risk tolerance needed). Age 55-60 (30-35 year horizon): 3.5% rate safe. Age 60+ (20-25 year horizon): 4-4.5% rate acceptable. Age 70+ (15 year horizon): Can use 5%+ rate (short runway). Risk-Averse Profile: Use 3% regardless of age. Aggressive/Flexible: Use 4.5-5% but maintain 3-year emergency buffer.",
        },
        {
          title: "Fixed vs Inflation-Adjusted Withdrawal Impact",
          body: "Fixed Withdrawal: Extract ₹83K/month for 30 years (simple, stable). But purchasing power erodes: ₹83K becomes ₹50K real value in 10 years (at 5% inflation). Inflation-Adjusted Withdrawal: Increase to ₹87K in Year 2, ₹91K in Year 3, etc. Preserves purchasing power but depletes corpus faster. Hybrid: Withdraw fixed amount first 10 years, adjust for inflation thereafter. Most advisors recommend inflation adjustment for early retirement (45-50).",
        },
        {
          title: "Asset Allocation Impact on Sustainable Income",
          body: "Conservative (70% debt): 5-6% expected returns → lower safe withdrawal rate. Balanced (50-50): 7-8% returns → moderate withdrawal. Aggressive (70% equity): 10-12% returns → higher withdrawal possible. But aggressive portfolios have downside risk. Sequence-of-returns risk is critical: Market crash in Year 1-3 of retirement cripples withdrawal sustainability. Mitigate: Keep 3-5 years expenses in debt/FD, regardless of asset allocation.",
        },
        {
          title: "Real-World Adjustments to Withdrawal Rates",
          body: "Income Drop Years: Can reduce withdrawal 10-20% if market crashes (retain flexibility). Healthcare Inflation: Budget 10-15% higher for medical expenses post-65. Longevity Planning: If family history suggests 95+ years, reduce withdrawal rate 0.5-1%. Pension/Social Income: If receiving ₹50K/month pension, corpus withdrawal reduced to ₹30-50K/month. Customize based on personal factors; 4% is guide, not gospel.",
        },
      ],
      faqs: [
        {
          question:
            "What is a sustainable withdrawal rate?",
          answer:
            "3-4% per annum is safe. 4-5% is moderate. Above 5% is risky (may deplete corpus before death). Use 3-3.5% for early retirement, 4-4.5% for age 60+.",
        },
        {
          question:
            "Should I withdraw more if I live modestly?",
          answer:
            "Yes, absolutely. This is a maximum sustainable rate. If your expenses are lower, you can withdraw more or preserve corpus for heirs/inflation buffer.",
        },
        {
          question:
            "What if I'm very risk-averse and want guaranteed income?",
          answer:
            "Buy an annuity with 40-50% of corpus (fixed income for life). Keep 50-60% in conservative investments (FD/bonds) for flexibility. Hybrid approach is safest.",
        },
        {
          question:
            "How does inflation affect my sustainable income?",
          answer:
            "Without inflation adjustment, purchasing power falls 5%/year. With inflation adjustment, withdrawal increases yearly, but corpus depletes faster. Choose based on inflation expectations.",
        },
        {
          question:
            "Can I adjust my withdrawal if markets crash?",
          answer:
            "Yes! Flexibility is key. If markets fall 30%, reduce withdrawal by 10-20% for 1-2 years till recovery. This 'dynamic withdrawal' sustains corpus longer than fixed withdrawal.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-corpus-calculator",
        "retirement/annuity-calculator",
        "investment/swp-calculator",
      ],
    },
  },

  {
    slug: "inflation-adjusted-corpus",
    category: "retirement",
    title: "Inflation-Adjusted Retirement Corpus",
    shortTitle: "Corpus Adjusted for Inflation",
    description:
      "Account for inflation impact when calculating retirement corpus target",
    keywords: [
      "inflation adjusted",
      "retirement corpus inflation",
      "future expense",
      "retirement planning inflation",
    ],
    inputs: [
      {
        type: "number",
        key: "currentExpense",
        label: "Current Annual Expense",
        prefix: "₹",
        min: 100000,
        max: 100000000,
        step: 50000,
        default: 1200000,
      },
      {
        type: "number",
        key: "yearsToRetirement",
        label: "Years Until Retirement",
        suffix: "years",
        min: 1,
        max: 50,
        step: 1,
        default: 20,
      },
      {
        type: "number",
        key: "inflationRate",
        label: "Expected Inflation Rate",
        suffix: "%",
        min: 1,
        max: 15,
        step: 0.5,
        default: 5,
      },
      {
        type: "number",
        key: "retirementYears",
        label: "Retirement Duration",
        suffix: "years",
        min: 10,
        max: 60,
        step: 1,
        default: 30,
      },
    ],
    calculate(v) {
      const expense = v.currentExpense as number;
      const yearsToRetire = v.yearsToRetirement as number;
      const inflation = (v.inflationRate as number) / 100;
      const retireYears = v.retirementYears as number;

      // Expense at retirement (inflated)
      const expenseAtRetirement = expense * Math.pow(1 + inflation, yearsToRetire);

      // Corpus needed using annuity at 5% return
      const returnRate = 0.05;
      const months = retireYears * 12;
      const monthlyRate = returnRate / 12;

      const corpus =
        expenseAtRetirement *
        ((1 - Math.pow(1 + monthlyRate, -months)) / monthlyRate);

      return {
        currentExpense: Math.round(expense),
        expenseAtRetirement: Math.round(expenseAtRetirement),
        corpusNeeded: Math.round(corpus),
        inflationFactor: Math.round(Math.pow(1 + inflation, yearsToRetire) * 100) / 100,
      };
    },
    resultFields: [
      {
        key: "corpusNeeded",
        label: "Inflation-Adjusted Corpus Needed",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "expenseAtRetirement",
        label: "Annual Expense at Retirement",
        format: "currency",
      },
      {
        key: "inflationFactor",
        label: "Inflation Multiplier",
        format: "number",
      },
    ],
    content: {
      whatIs:
        "Inflation is retirement planning's silent assassin: the purchasing power erosion that many retirees underestimate until too late. This calculator highlights the brutal math of inflation: your ₹1 lakh current annual expense becomes ₹2.65 lakh at just 5% inflation over 20 years. If you retire at 45 with ₹1.5 crore corpus (adequate for ₹75,000/month today), by age 65 that same lifestyle costs ₹120,000+/month—and by age 75, ₹190,000/month. Indian inflation averages 5-6% (with healthcare and education inflation at 8-10%), making inflation-adjusted corpus planning absolutely critical. This calculator works backwards from your current lifestyle expenses, inflates them to your retirement year, then calculates the exact corpus needed to sustain that inflated lifestyle over 30-40 retirement years. It answers the essential question: 'How much corpus do I really need, accounting for rupee devaluation over two decades?'",
      sections: [
        {
          title: "The Exponential Impact of Inflation Over 20-30 Years",
          body: "₹100/year expense at 5% inflation: Year 10 = ₹163 (63% increase), Year 20 = ₹265 (165% increase), Year 30 = ₹432 (332% increase). This exponential growth is why retirees with seemingly adequate corpus often face financial stress in their 80s. Lesson: Don't use today's expense numbers for 20-30 year retirement planning. Always inflate.",
        },
        {
          title: "India-Specific Inflation Rates by Category",
          body: "General CPI Inflation: 4-6% (RBI target 4%). Food/Essentials: 5-7% (volatile). Healthcare: 8-10% (fastest growing). Education: 8-10%. Housing/Rent: 6-8%. Energy/Utilities: 5-7%. Weighted approach for retirees: Healthcare portion (20% of expenses) at 10%, essentials (60%) at 5%, discretionary (20%) at 4% = blended ~6.2% effective inflation.",
        },
        {
          title: "Conservative vs Aggressive Inflation Assumptions",
          body: "Conservative (7% inflation): Assumes higher-than-historical rates; safer planning. Moderate (5-6% inflation): RBI target; reasonable for 20-year horizon. Aggressive (4% inflation): Optimistic; risky for early retirees. Recommendation: Use 6-7% for planning, even if RBI targets 4% (volatility safety). Overestimating inflation yields surplus corpus (better than shortfall).",
        },
        {
          title: "Healthcare Inflation: The Hidden Retirement Killer",
          body: "Healthcare inflation in India runs 8-10% (double general inflation). A 65-year-old needing ₹2L/year healthcare today will need ₹4.3L/year at 75 (10 years at 8% inflation). Pre-Medicare (before 60-65), premium-quality healthcare is expensive. Allocate 20-25% of retirement corpus to healthcare buffer, separate from daily expense planning.",
        },
        {
          title: "Strategies to Combat Retirement Inflation Erosion",
          body: "Inflation-Linked Withdrawals: Increase SWP by 5-6% annually, reducing corpus but maintaining purchasing power. Post-Retirement Investment: Maintain 20-30% equity exposure in retirement for inflation-beating returns (7-8% real). Pension + SWP Hybrid: Government/EPS pensions nominally indexed; combine with SWP for comprehensive inflation hedge. Location Arbitrage: Retire in lower-cost city/tier-2 town; lifestyle inflation less severe.",
        },
      ],
      faqs: [
        {
          question:
            "How much does inflation impact my corpus need?",
          answer:
            "Significantly. At 5% inflation over 20 years, expenses triple. If you don't account for inflation, your corpus runs out early. Always include inflation in planning.",
        },
        {
          question:
            "Should I use historical or expected inflation?",
          answer:
            "Use expected (future) inflation, not historical. RBI targets 4%. Use 4-6% for planning. If uncertain, use 5% (middle estimate). Conservative planners use 6-7%.",
        },
        {
          question:
            "What if my expenses increase faster than inflation?",
          answer:
            "E.g., healthcare inflation (8-10%) > general inflation (5%). Budget extra for healthcare. Add ₹10-20L buffer for unexpected big expenses (surgery, emergency).",
        },
        {
          question:
            "Can I retire with less if inflation is low?",
          answer:
            "Possibly, but risky. You can't control inflation. Plan for 5-6% to be safe. If inflation comes in lower, extra corpus is a bonus.",
        },
        {
          question:
            "Should my investments beat inflation for retirement?",
          answer:
            "Yes, essential. If your corpus grows at 5% but inflation is 5%, real growth = 0%. Target 6-7% real return (9-12% nominal). This preserves and grows wealth.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-corpus-calculator",
        "investment/real-return-calculator",
        "investment/inflation-adjusted-return",
      ],
    },
  },

  {
    slug: "withdrawal-rate-calculator",
    category: "retirement",
    title: "Safe Withdrawal Rate Calculator",
    shortTitle: "Safe Withdrawal Rate",
    description:
      "Determine sustainable withdrawal rate based on age & retirement horizon",
    keywords: [
      "withdrawal rate",
      "safe withdrawal",
      "sustainable withdrawal",
      "4% rule",
      "retirement income planning",
    ],
    inputs: [
      {
        type: "number",
        key: "corpus",
        label: "Retirement Corpus",
        prefix: "₹",
        min: 100000,
        max: 1000000000,
        step: 100000,
        default: 5000000,
      },
      {
        type: "number",
        key: "lifeExpectancy",
        label: "Life Expectancy",
        suffix: "years",
        min: 65,
        max: 100,
        step: 1,
        default: 85,
      },
      {
        type: "number",
        key: "currentAge",
        label: "Current Age",
        suffix: "years",
        min: 50,
        max: 95,
        step: 1,
        default: 60,
      },
      {
        type: "number",
        key: "expectedReturn",
        label: "Expected Return in Retirement",
        suffix: "% p.a.",
        min: 0,
        max: 12,
        step: 0.5,
        default: 5,
      },
    ],
    calculate(v) {
      const corpus = v.corpus as number;
      const lifeExp = v.lifeExpectancy as number;
      const age = v.currentAge as number;
      const returnRate = (v.expectedReturn as number) / 100;

      const years = lifeExp - age;

      // Safe withdrawal rate formula
      // Using 3-4% for age 60+, 2.5-3% for age <60
      let baseRate = 0.04;
      if (age < 55) baseRate = 0.025;
      else if (age < 60) baseRate = 0.03;

      // Adjust for return rate (higher returns allow higher withdrawal)
      const adjustedRate = baseRate + returnRate * 0.2;

      const safeAnnualWithdrawal = corpus * adjustedRate;
      const safeMonthlyWithdrawal = safeAnnualWithdrawal / 12;

      return {
        corpus: Math.round(corpus),
        safeWithdrawalRate: Math.round(adjustedRate * 10000) / 100,
        safeAnnualWithdrawal: Math.round(safeAnnualWithdrawal),
        safeMonthlyWithdrawal: Math.round(safeMonthlyWithdrawal),
        yearsOfRetirement: years,
      };
    },
    resultFields: [
      {
        key: "safeWithdrawalRate",
        label: "Safe Withdrawal Rate",
        format: "percent",
        isPrimary: true,
      },
      {
        key: "safeMonthlyWithdrawal",
        label: "Safe Monthly Withdrawal",
        format: "currency",
      },
      {
        key: "safeAnnualWithdrawal",
        label: "Safe Annual Withdrawal",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "The safe withdrawal rate is the maximum percentage of your retirement corpus you can sustainably withdraw each year (adjusted for inflation) without depleting it before your expected lifespan. This is arguably the single most important metric for retirement success, yet most Indians overlook it. The famous 4% rule—originating from the Trinity Study (1998)—states that withdrawing 4% of your initial corpus annually, adjusted for inflation, has a 95% success rate over 30-year retirement horizons in Western markets. However, for Indian retirees facing 5-6% inflation, healthcare inflation of 8-10%, and equity market volatility, the prudent rate is 3-3.5%. This calculator determines your specific safe withdrawal rate based on your age, life expectancy, return expectations, and inflation, helping you decide: Can I retire now? Should I work 2-3 more years? Do I need to adjust my lifestyle expectations?",
      sections: [
        {
          title: "Understanding the 4% Rule & India Adaptation",
          body: "Trinity Study (1998): 4% annual withdrawal (inflation-adjusted) succeeded 95% of the time over 30 years with 60% stocks/40% bonds portfolio. Formula: Year 1 = Corpus × 4%, Year 2 = Year 1 × (1 + inflation), Year 3 = Year 2 × (1 + inflation). India Adaptation: Use 3-3.5% rate due to higher inflation (5-6%), longer life expectancy trends, and more volatile equity markets. Conservative approach: 3% for early retirees (45-50), 3.5% for standard retirement (60-65), 4% for late retirement (70+).",
        },
        {
          title: "Safe Withdrawal Rate by Age & Time Horizon",
          body: "Age 45 (45-year horizon): 2.5-3% safe rate (longest runway, sequence risk critical). Age 50 (35-40 year): 3% safe rate. Age 60 (25-30 year): 3.5% safe rate. Age 70 (15-20 year): 4-4.5% safe rate. Age 75+ (10-15 year): 5-6% safe rate (short remaining years). Rule: Longer time horizon = lower safe rate (protects against prolonged market downturns early in retirement).",
        },
        {
          title: "How Portfolio Returns Affect Safe Withdrawal Rate",
          body: "Conservative Portfolio (5% return, 70% debt): 2.5-3% safe withdrawal. Balanced Portfolio (7% return, 50-50): 3.5-4% safe withdrawal. Growth Portfolio (10% return, 70% equity): 4.5-5% safe withdrawal. Important: Assumes you maintain allocation. Market crashes reduce returns, requiring lower withdrawal rates. Recommendation: Use realistic return assumptions (6-7% for balanced, not optimistic 10-12%).",
        },
        {
          title: "Dynamic vs Fixed Withdrawal Strategies",
          body: "Fixed Withdrawal: Withdraw same % every year (e.g., 3.5% first year, then adjusted for inflation). Simple, predictable, but rigid. Dynamic Withdrawal: Adjust based on portfolio performance. Good year → increase withdrawal 5-10%; bad year → reduce 5-10%. More complex but extends corpus life in volatile markets. Bucket Strategy: 3-year living expenses in cash, 5-10 years in bonds, 10+ years in equity; rebalance annually.",
        },
        {
          title: "Monitoring & Adjusting Withdrawal Rate in Retirement",
          body: "Annual Review: Check portfolio performance. If down >20% in year, reduce withdrawal 5-10%. If up >30%, consider small increase. Mid-Course Corrections: Every 5 years, recalculate safe rate based on updated life expectancy, inflation trends, portfolio returns. Longevity Surge: If you reach 75-80 in good health, you can modestly increase withdrawal (shortened horizon). Lifestyle Flexibility: Be prepared to reduce discretionary spending 10-20% if markets crash.",
        },
      ],
      faqs: [
        {
          question: "Why is 4% considered safe?",
          answer:
            "Historical studies show 4% withdrawal has 95% success rate over 30 years with balanced portfolio (60% stocks, 40% bonds). In India, use 3.5% for safety due to higher inflation.",
        },
        {
          question:
            "What if I need more than the safe withdrawal rate?",
          answer:
            "Options: (a) increase corpus via more savings, (b) reduce expenses, (c) supplement with part-time work, (d) take calculated risk with 4-5% withdrawal (80% success).",
        },
        {
          question:
            "Should I increase withdrawal if markets perform well?",
          answer:
            "Not fixed. Use 'dynamic withdrawal': if portfolio gains 20% in year 1, you can increase withdrawal by 5-10%. In loss years, decrease withdrawal 5-10%. This extends corpus life.",
        },
        {
          question:
            "How does inflation affect safe withdrawal?",
          answer:
            "Safe rate assumes you increase withdrawal by inflation each year. E.g., start 4% of ₹50L = ₹2L/year. Next year at 5% inflation, withdraw ₹2.1L. Budget for this increase.",
        },
        {
          question:
            "Is 4% rule applicable to India with high inflation?",
          answer:
            "No, use 3-3.5% for India (inflation ~5-6%). If inflation rises to 7%, use 2.5-3%. Conservative planning is safer than hoping for 12% returns.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-income-calculator",
        "retirement/annuity-calculator",
        "investment/swp-calculator",
      ],
    },
  },

  {
    slug: "senior-citizen-savings",
    category: "retirement",
    title: "Senior Citizen Savings Options Comparison",
    shortTitle: "Senior Savings",
    description:
      "Compare government senior schemes and options for safe investment returns",
    keywords: [
      "senior citizen savings",
      "elderly investments",
      "post-60 savings",
      "senior schemes",
    ],
    inputs: [
      {
        type: "number",
        key: "investmentAmount",
        label: "Investment Amount",
        prefix: "₹",
        min: 100000,
        max: 1000000000,
        step: 100000,
        default: 1000000,
      },
      {
        type: "select",
        key: "investmentOption",
        label: "Investment Option",
        options: [
          { value: "scss", label: "SCSS (8.2% - 5 years)" },
          { value: "fd", label: "Senior FD (6-7% - any period)" },
          { value: "mis", label: "Post Office MIS (7.1% - 5 years, monthly income)" },
          { value: "nps", label: "NPS with pension (variable - till life)" },
          { value: "svp", label: "SVP - Senior Voluntary Pension (8% - life insurance)" },
        ],
        default: "scss",
      },
      {
        type: "number",
        key: "investmentYears",
        label: "Investment Period",
        suffix: "years",
        min: 1,
        max: 20,
        step: 1,
        default: 5,
      },
    ],
    calculate(v) {
      const amount = v.investmentAmount as number;
      const option = v.investmentOption as string;
      const years = v.investmentYears as number;

      let maturityAmount = 0;
      let monthlyIncome = 0;
      let description = "";

      if (option === "scss") {
        // 8.2% compounded
        maturityAmount = amount * Math.pow(1.082, years);
        monthlyIncome = (maturityAmount - amount) / (years * 12);
        description = "Safe, government-backed";
      } else if (option === "fd") {
        // 6.5% average
        maturityAmount = amount * Math.pow(1.065, years);
        monthlyIncome = (maturityAmount - amount) / (years * 12);
        description = "Bank FD for seniors";
      } else if (option === "mis") {
        // 7.1% MIS
        maturityAmount = amount * Math.pow(1.071, years);
        monthlyIncome = (amount * 0.071) / 12;
        description = "Post office, monthly income";
      } else if (option === "nps") {
        // Variable returns, 40% annuity
        maturityAmount = amount * Math.pow(1.07, years);
        monthlyIncome = (maturityAmount * 0.4) / 100;
        description = "Flexible, lifetime pension";
      } else {
        // SVP
        maturityAmount = amount * Math.pow(1.08, years);
        monthlyIncome = (maturityAmount * 0.08) / 12;
        description = "Life insurance + pension";
      }

      return {
        investmentAmount: Math.round(amount),
        maturityAmount: Math.round(maturityAmount),
        interest: Math.round(maturityAmount - amount),
        monthlyIncome: Math.round(monthlyIncome),
        option: option,
      };
    },
    resultFields: [
      {
        key: "maturityAmount",
        label: "Maturity Amount",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "monthlyIncome",
        label: "Expected Monthly Income",
        format: "currency",
      },
      {
        key: "interest",
        label: "Total Interest/Gains",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "India's government recognizes that seniors (60+) face unique financial needs: income security, capital preservation, and healthcare costs. Therefore, it offers specialized investment schemes with preferential interest rates (6-8.2%) and tax benefits unavailable to younger investors. SCSS (Senior Citizen Savings Scheme) at 8.2% is among the safest fixed-income options in India, backed by government guarantee. Post Office Monthly Income Scheme (MIS) at 7.1% provides monthly pension-like income. Senior Citizen Fixed Deposits offer 6-7% returns with liquidity. These schemes are particularly attractive because they combine safety (government/bank guarantee), superior returns (2-3% higher than regular FDs), and tax-deductible interest (under Section 80TTB, up to ₹50,000 deduction). This calculator helps seniors compare maturity amounts and monthly income across options, enabling informed allocation decisions. Most advisors recommend a diversified approach: 50% SCSS (growth + safety), 30% MIS (monthly income), 20% senior FD (emergency liquidity).",
      sections: [
        {
          title: "SCSS: The Gold Standard for Senior Safety & Returns",
          body: "Senior Citizen Savings Scheme, regulated by PFRDA: 8.2% interest (best-in-class), 5-year maturity (3-year extension possible), government-backed security, compounded quarterly, fully tax-deductible under Section 80TTB (up to ₹50K interest deductible). Minimum ₹1000, maximum ₹30 lakh. Ideal for: corpus that can be locked 5 years, desire for maximum returns with safety. Early exit (after 1 year): 1.5% penalty; after 5 years: no penalty.",
        },
        {
          title: "Post Office MIS: Monthly Income Like Pension",
          body: "Post Office Monthly Income Scheme: 7.1% returns, 5-year tenure, guarantees monthly income (principal untouched). Minimum ₹1,000, maximum ₹9 lakh. Monthly income = Principal × Rate / 12. Example: ₹10L investment → ₹5,917/month income. Tax-deductible (Section 80TTB). Ideal for: seniors needing regular monthly cash flow (pension-like replacement). After 5 years, can exit or reinvest.",
        },
        {
          title: "Senior Citizen Fixed Deposits: Liquidity with Good Returns",
          body: "Banks offer 6-7% on fixed deposits for seniors (0.5-1% premium over regular FDs). Fully liquid (can withdraw anytime; penalty only if before maturity). DEPOSITS insured by DICGC (up to ₹5L per account/bank). Ideal for: seniors needing emergency access while earning better returns than savings account (3-4%). Tax on interest at slab rate (not deductible like SCSS/MIS).",
        },
        {
          title: "NPS for Seniors: Flexible Corpus with Annuity Option",
          body: "NPS continues post-60; in fact, many seniors increase NPS contributions (special ₹2L deduction for 50+). At 60, can withdraw 60% as lumpsum (tax-free/minimal tax), convert 40% to annuity (monthly pension). Expected returns 7% (balanced allocation). Ideal for: seniors wanting flexibility + lifetime pension security. Can start fresh NPS even at 70.",
        },
        {
          title: "Comparative Strategy: Diversified Senior Portfolio",
          body: "Typical senior allocation (₹50L corpus): 25L in SCSS (8.2%, locked, maximum growth), 15L in MIS (7.1%, monthly ₹8,875 income), 10L in senior FD (6.5%, emergency access), 5-10L remaining for NPS/investments. This yields: ₹2.05L+ annual returns (~4% average), ₹8,875/month passive income, full capital security, high liquidity. Reviewed/rebalanced annually based on life expectancy, health needs, inflation.",
        },
      ],
      faqs: [
        {
          question:
            "What is the best investment for seniors?",
          answer:
            "No single best; depends on needs. SCSS for safety + 8.2% growth. MIS for monthly income. Combination: invest 50% SCSS, 30% MIS, 20% senior FD for diversification.",
        },
        {
          question:
            "Can seniors invest in stocks/mutual funds?",
          answer:
            "Yes, but conservative allocation: 30-40% equity, 50-60% debt, 10% gold. SIP in conservative funds better than lumpsum. Consult advisor for personalized plan.",
        },
        {
          question:
            "Is senior FD better than SCSS?",
          answer:
            "SCSS: 8.2%, 5-year lock-in. Senior FD: 6-7%, fully liquid. SCSS if you can lock capital; FD if you need emergency access. SCSS for growth, FD for liquidity.",
        },
        {
          question:
            "What about healthcare cost planning for seniors?",
          answer:
            "Critical. Budget ₹1-3L/year for healthcare. Get health insurance (₹30-50L cover, premium ₹5-10K/year). Allocate 10-15% of corpus as health emergency fund.",
        },
        {
          question:
            "Can seniors still buy life insurance?",
          answer:
            "Yes, till age 75 for most. Term insurance cheaper than whole-life. Consider: ₹50-100L cover to cover estate taxes + family needs. Consult insurance advisor.",
        },
      ],
      relatedSlugs: [
        "savings/scss-returns",
        "savings/post-office-mis",
        "savings/fd-calculator",
        "investment/nps-calculator",
      ],
    },
  },

  {
    slug: "retirement-gap-calculator",
    category: "retirement",
    title: "Retirement Gap Calculator",
    shortTitle: "Retirement Shortfall",
    description:
      "Quantify your retirement savings shortfall and plan to close it systematically",
    keywords: [
      "retirement gap",
      "retirement shortfall",
      "retirement deficit",
      "retirement planning gap",
    ],
    inputs: [
      {
        type: "number",
        key: "targetCorpus",
        label: "Target Retirement Corpus Needed",
        prefix: "₹",
        min: 500000,
        max: 1000000000,
        step: 100000,
        default: 5000000,
      },
      {
        type: "number",
        key: "currentCorpus",
        label: "Current Retirement Savings",
        prefix: "₹",
        min: 0,
        max: 1000000000,
        step: 100000,
        default: 1000000,
      },
      {
        type: "number",
        key: "yearsToRetirement",
        label: "Years Until Retirement",
        suffix: "years",
        min: 1,
        max: 50,
        step: 1,
        default: 15,
      },
      {
        type: "number",
        key: "expectedReturn",
        label: "Expected Annual Return",
        suffix: "% p.a.",
        min: 1,
        max: 20,
        step: 0.5,
        default: 10,
      },
    ],
    calculate(v) {
      const target = v.targetCorpus as number;
      const current = v.currentCorpus as number;
      const years = v.yearsToRetirement as number;
      const returnRate = (v.expectedReturn as number) / 100;

      // Future value of current corpus
      const futureValue = current * Math.pow(1 + returnRate, years);

      // Gap
      const gap = Math.max(0, target - futureValue);

      // Monthly SIP needed to bridge gap
      const monthlyRate = returnRate / 12;
      const months = years * 12;

      let monthlySIP = 0;
      if (monthlyRate > 0 && gap > 0) {
        monthlySIP =
          gap /
          ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate / (1 +
            monthlyRate));
      }

      return {
        targetCorpus: Math.round(target),
        currentCorpus: Math.round(current),
        projectedCorpus: Math.round(futureValue),
        shortfall: Math.round(gap),
        monthlySIPNeeded: Math.round(monthlySIP),
        yearsToRetirement: years,
        bridgeable: gap <= futureValue * 2,
      };
    },
    resultFields: [
      {
        key: "shortfall",
        label: "Retirement Savings Gap",
        format: "currency",
        isPrimary: true,
      },
      {
        key: "monthlySIPNeeded",
        label: "Monthly SIP to Bridge Gap",
        format: "currency",
      },
      {
        key: "projectedCorpus",
        label: "Projected Corpus at Retirement",
        format: "currency",
      },
    ],
    content: {
      whatIs:
        "The retirement gap is perhaps the most sobering metric in financial planning: the difference between the corpus you need and what you'll actually have at retirement. For many Indian professionals, this gap is substantial and non-negotiable, demanding urgent corrective action. This calculator projects your current savings forward (at assumed returns) and compares to your target corpus, revealing the exact shortfall. For example, a 35-year-old with ₹25 lakh saved, targeting ₹1 crore corpus by 60, with 10% expected returns, will have ₹1.36 crore at retirement—no gap. However, if their target is ₹2 crore, the gap is ₹64 lakh, requiring ₹35,000/month SIP. The calculator then presents three critical options to bridge this gap: (1) increase monthly savings, (2) extend retirement timeline (often the most powerful lever), or (3) reduce target lifestyle expenses. Most Indians face some retirement gap; acknowledging it early and taking corrective action is the difference between retirement anxiety and confidence.",
      sections: [
        {
          title: "Understanding Retirement Gap: The Gap Math",
          body: "Gap = Target Corpus - Future Value of Current Savings. Example: Target ₹1Cr, current ₹30L at 10% returns over 15 years = ₹1.27Cr future value → Gap = 0 (surplus). If Target ₹2Cr instead, Gap = ₹73L. Zero gap = retirement ready. Small positive gap = manageable via modest SIP. Large gap (>₹50L) = requires aggressive SIP or extended working years. Negative gap (surplus) = can reduce SIP, retire earlier, or increase lifestyle.",
        },
        {
          title: "Three Levers to Bridge Retirement Gap",
          body: "Lever 1 - Increase SIP: Calculator shows monthly SIP needed to bridge gap. If ₹2L/month SIP seems unaffordable, leverage bonus/increment. Lever 2 - Extend Timeline: Working 2-3 more years compounds returns significantly, often halving required SIP. Lever 3 - Reduce Target: Living on ₹70K/month instead of ₹100K reduces target corpus 30%, closing gaps substantially. Most successful retirees use combination: increase SIP 30% + work 2 more years + reduce lifestyle 10%.",
        },
        {
          title: "The Power of Extending Working Years",
          body: "Each additional year of work has 3 effects: (a) 1 more year of compound returns on corpus, (b) 1 fewer year of retirement withdrawals, (c) continued income to save. Example: 15-year gap of ₹50L can be bridged by ₹25K/month SIP, OR by working 2 years longer (gap becomes ₹0 due to compound growth + 24 fewer months of expense). Extending 2-3 years is often the easiest adjustment without lifestyle compromise.",
        },
        {
          title: "How Pension Income Reduces Required Gap",
          body: "Pension (EPS, APY, NPS annuity) provides guaranteed income, reducing corpus need. Example: If EPS gives ₹50K/month (₹6L/year), it covers basic expenses, allowing retirement corpus to fund only discretionary spending (₹3-4L/year). Original ₹1Cr target reduces to ₹50L. This massive reduction often eliminates gap entirely. Strategy: First secure pension income sources; gap closure becomes easier.",
        },
        {
          title: "Action Plan When Facing Large Retirement Gap",
          body: "If gap > ₹50L: (a) Work 2-3 more years (compound growth closes gap, (b) Increase SIP by 20-30% aggressively, (c) Target 12%+ returns via balanced equity allocation (accept higher volatility), (d) Reduce target lifestyle by 15-25%. If gap > ₹100L: Seriously consider extending 3-5 years; cannot be bridged via SIP alone without unrealistic discipline. If gap < ₹20L: Easily bridged via ₹10-15K/month modest SIP increase or annual bonus allocation.",
        },
      ],
      faqs: [
        {
          question:
            "If my gap is ₹1 crore, is retirement still possible?",
          answer:
            "Depends on context. If you need ₹3Cr corpus and will have ₹2Cr, gap = ₹1Cr. You can: (a) increase SIP by ₹30K/month for 25 months, (b) work 2 more years, (c) reduce retirement expenses by 30%, or (d) combination of all.",
        },
        {
          question:
            "What if I can't save the monthly amount to bridge the gap?",
          answer:
            "Adjust these levers: (a) increase expected returns (higher risk investments like equity), (b) extend retirement by 2-5 years (huge impact), (c) reduce target lifestyle, (d) earn side income.",
        },
        {
          question:
            "Should I increase risk investments to earn 15% and close the gap?",
          answer:
            "Not necessarily. Higher returns = higher risk of losses. Balanced approach: 60% equity (10-12% return), 40% debt (5-7% return) = 8-9% blended return is realistic without excessive risk.",
        },
        {
          question:
            "Is it ever too late to bridge retirement gap?",
          answer:
            "No, but urgent. If 10 years to retirement, aggressive saving + high returns can work. If 3 years, may not be feasible without drastic changes. Work longer (1-2 years) is often the best option.",
        },
        {
          question:
            "How do I account for pension income in retirement gap?",
          answer:
            "Reduce target corpus by PV of pension. If EPS pension = ₹50K/month, value at 6% return = ₹1Cr approx. So if target was ₹5Cr, adjust to ₹4Cr for gap calculation.",
        },
      ],
      relatedSlugs: [
        "retirement/retirement-corpus-calculator",
        "retirement/retirement-planning",
        "retirement/fire-calculator",
      ],
    },
  },
];
